import { responseSubjects } from '../internal/response-subjects.js';
/**
 * T15102 · TanStack realtime-hook option triple-override.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1296 — every realtime-capable hook MUST set THREE TanStack options to
 * close the hydration-vs-first-NATS-push race (TanStack issue #9642):
 *
 *   1. `retryOnMount: false`
 *      A failed prefetch on the server would otherwise auto-retry on the
 *      client and shadow the NATS-driven first paint.
 *
 *   2. `staleTime: Number.POSITIVE_INFINITY`
 *      The realtime channel — not the clock — drives invalidation.
 *      `staleTime: 0` would refetch on every mount and trample fresh
 *      NATS-applied state.
 *
 *   3. `refetchOnMount: (query) => query.state.dataUpdatedAt < connectivity.lastReconnectAt`
 *      MUST be a FUNCTION, NEVER the literal `'always'`. The literal
 *      short-circuits the staleTime gate per
 *      `query-core/queryObserver.ts::shouldFetchOn()` and would storm 50
 *      GETs on a 50-card list cold-mount. The function returns `true` only
 *      when the row's data predates the most-recent NATS reconnect — i.e.
 *      reconnect re-syncs, but mount-during-streaming does not.
 */
import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { markNextWrite } from '../../devtool/channels/cache-write.js';
import { isDevtoolEnabled, sdkEvents } from '../../devtool/events.js';
import { useRealtimeBufferResourceReader } from '../../devtool/hooks/resource-usage-reader-hooks.js';
import { _bumpPerf, _enqueueApplyPerf, _flushApplyPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';
import { attachFenceToResult, readLiveSeq, tagNewRowsFromDiff } from '../../nats/live-since-mount-store.js';
import { scopedSnapshotSequence } from '../../nats/scoped-snapshot-sequence.js';
import { admitEvent, gateKey, seedGate, wouldAdmitEvent } from '../../nats/seq-gate-store.js';
import { base64ToCursor, compareCursor, extractSequenceId, SEQUENCE_ID_LEN } from '../../nats/sequence-id.js';
import { readSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';
import { connectivityStore } from '../connectivity-store.js';
import { isDebugOverridden } from '../debug-override-registry.js';
import { matchSubject } from '../dispatch/createEventDispatcher.js';
import { hasActiveFetchOnceRealtimeOwner, hasCompletedFetchOnceRealtimeSnapshot, markFetchOnceRealtimeSnapshotComplete, resetFetchOnceRealtimeSnapshot, retainFetchOnceRealtimeOwner, runFetchOnceRealtimeSnapshot, subscribeFetchOnceRealtimePolicy } from '../internal/fetch-once-realtime-policy.js';
import { keepPreviousDataAcrossQueryKeys } from '../internal/query-placeholder-data.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import { lastSnapshotFetchWasPostWire, recordSnapshotFetchStart } from '../internal/snapshot-wire-fence.js';
import { filterTerminalRows, recordTerminalRow } from '../internal/terminal-row-fence.js';
import { hasPollingCoverage } from '../polling/coverage.js';
import { observePollingFetch, requirePollingRecovery } from '../polling/state.js';
/** Subscribe to the connectivity store's `state` field. The selector
 * returns a stable string so React skips re-renders when state is
 * unchanged across store writes that touched other fields. */
function subscribeConnectivityState(cb) {
  return connectivityStore.subscribe((s, prev) => {
    if (s.state !== prev.state) cb();
  });
}
function getConnectivityStateSnapshot() {
  return connectivityStore.getState().state;
}
import { degradedPollInterval } from '../../connectivity/degraded/degraded-poll-fallback.js';
import { emit as devtoolEmit } from '../../devtool/events.js';
import { defaultSubjectGateId } from '../../nats/registry/subject-gate-id.js';
import { RegistryContext } from '../context/RegistryContext.js';
/** Dev warning timeout (file 05 §L370) — engineers see this immediately. */
const NEVER_BOUND_DEV_MS = 5_000;
/** Prod telemetry timeout — slower-firing safety net, rate-limited per spec. */
const NEVER_BOUND_PROD_MS = 30_000;
/**
 * Phase 2 · cap on events buffered before the first snapshot resolves
 * (subscribe-before-snapshot race). Bounds memory if a snapshot never
 * lands; on overflow buffering stops and excess events are dropped.
 */
const PRE_SNAPSHOT_BUFFER_CAP = 2048;
/** Bounded retries keep a reconnecting consumer from leaving a stale cache. */
const HANDOFF_RECOVERY_RETRY_MS = [250, 500, 1_000, 2_000, 5_000];
/**
 * Bounded wait before the wire-handoff gate force-opens so REST data still
 * loads when NATS is degraded (bootstrap failed, hung, or reconnecting).
 * Sized above the registry's documented cold-bootstrap window (~1-2s for
 * the dynamic JetStream import + `consumers.add`) so healthy cold mounts
 * never trip it. Exported for tests.
 */
export const WIRE_HANDOFF_DEADLINE_MS = 5_000;
/**
 * Per-hook random spread added to the deadline and to the late-readiness
 * re-fence so N gated hooks mounted in the same commit do not stampede the
 * REST API in a single tick. Exported for tests.
 */
export const WIRE_HANDOFF_JITTER_MS = 500;
/** Failed topology reconciliation retries while the owning observer is mounted. */
const AUTHORITATIVE_REFETCH_RETRY_MS = [250, 500, 1_000, 2_000, 5_000];
function neverBoundDevtoolEmit(payload) {
  devtoolEmit('subscription:never-bound', payload);
}
function emitHandoffRecovery(specName, reason) {
  sdkEvents.emit('realtime:handoff-recovery', {
    specName,
    reason
  });
}
function handoffReasonFromError(error) {
  if (error !== null && typeof error === 'object' && 'reason' in error) {
    const reason = error.reason;
    if (reason === 'consumer_recreated') return reason;
  }
  return 'not_ready';
}
function snapshotCoversCursor(snapshot, queryHash, cursor) {
  if (snapshot.kind === 'cursor') return compareCursor(snapshot.cursor, cursor) >= 0;
  if (snapshot.kind !== 'unhandled') return false;
  const recorded = base64ToCursor(readSnapshotLastSeq(queryHash));
  return recorded !== null && compareCursor(recorded, cursor) >= 0;
}
function hasSnapshotFence(snapshot, queryHash) {
  if (snapshot.kind === 'cursor' || snapshot.kind === 'empty') return true;
  return snapshot.kind === 'unhandled' && base64ToCursor(readSnapshotLastSeq(queryHash)) !== null;
}
function neverBoundProdTelemetry(payload) {
  // Prod path emits on the SDK event bus; the platform's telemetry sink
  // (Datadog / Better Stack) subscribes to `sdk.viewport_unbound` at
  // app boot. The bus is DCE-guarded inside `devtoolEmit`.
  devtoolEmit('sdk.viewport_unbound', payload);
}
// ─────────────────────────────────────────────────────────────────────────
// Devtool dispatch tracing helpers — extracted to module scope so the
// `dispatchEvent` useCallback stays free of `let` bindings and the React
// Compiler keeps it in the optimisation set.
// ─────────────────────────────────────────────────────────────────────────
function nowMonotonic() {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
}
// ─────────────────────────────────────────────────────────────────────────
// Realtime invalidate-refetch throttle.
//
// `onSignal:invalidate` (and the pure-invalidate fallback) fire a REAL HTTP
// refetch on EVERY matching NATS event. For an aggregate hook bound to a
// high-frequency channel — e.g. `useGetMarketTraders` on a market's
// `…user.*.trade.v0` — that is one `GET /v1/markets/{m}/traders` per trade,
// a request storm under sustained trade flow (every mounted board × every
// trade × every market).
//
// These hooks (traders board, holdings, user stats) are server-computed
// aggregates that CANNOT be patched from the event payload — realized/
// unrealized PnL, rank, balances are all server-derived (see `DEFERRED.md`
// DEF-9 / DEF-1). `invalidate` is the correct recipe, but it does not need
// per-event granularity. This leading-edge throttle, keyed per queryKey,
// collapses a burst into: one refetch immediately, then at most one per
// `REFETCH_COALESCE_MS` while events keep arriving, plus a trailing refetch
// so the final state is never missed. The cache's stale flag is still
// flipped per-event (cheap, no network) by the caller; only the HTTP
// refetch is rate-limited here.
// ─────────────────────────────────────────────────────────────────────────
/** Minimum interval between realtime-driven HTTP refetches of one query. */
const REFETCH_COALESCE_MS = 2_000;
const _refetchThrottle = new Map();
/**
 * Leading-edge throttle around `refetchRealtimeQuery`, keyed per queryKey.
 * First call refetches immediately and opens a window; calls inside the
 * window set a trailing flag; when the window closes a pending trailing
 * refetch fires and re-arms, else the entry is dropped so the next event
 * after a quiet period is a fresh leading edge again.
 */
function throttledRealtimeRefetch(qc, key) {
  if (hasActiveFetchOnceRealtimeOwner(qc, key)) return;
  const h = hashKey(key);
  const existing = _refetchThrottle.get(h);
  if (existing !== undefined && existing.timer !== null) {
    existing.pending = true;
    existing.lastEventAt = Date.now();
    return;
  }
  void refetchRealtimeQuery(qc, key);
  const ensureTrailing = qc.getQueryCache().find({
    queryKey: key,
    exact: true
  })?.observers.some(observer => observer.options.enabled !== false && observer.options.meta?.reconcileTrailing === true) === true;
  const entry = {
    timer: null,
    pending: ensureTrailing,
    ensureTrailing,
    lastEventAt: Date.now()
  };
  const tick = () => {
    const cur = _refetchThrottle.get(h);
    if (cur === undefined) return;
    if (cur.pending) {
      // Sorted feeds retain the one-second API cache. Ensure the final read is
      // at least one throttle window after the last event, including late events.
      cur.pending = cur.ensureTrailing && (Date.now() - cur.lastEventAt < REFETCH_COALESCE_MS || qc.getQueryState(key)?.fetchStatus === 'fetching');
      void refetchRealtimeQuery(qc, key);
      cur.timer = setTimeout(tick, REFETCH_COALESCE_MS);
    } else {
      _refetchThrottle.delete(h);
    }
  };
  entry.timer = setTimeout(tick, REFETCH_COALESCE_MS);
  _refetchThrottle.set(h, entry);
}
// ─────────────────────────────────────────────────────────────────────────
// applyEvent dispatch coalescer.
//
// The `applyEvent` branch patches the cache on EVERY NATS event. A list
// query (`useGetMarkets`) shares ONE cache key across the whole feed, so a
// trade burst — the indexer publishes per-slot (~400ms) — fires N synchronous
// `setQueryData` calls, each running the full notify pass over every observer
// (100+ field leaves on `markets/list`). The visible result is identical: the
// `AnimatedNumber` tween + 600ms CSS bar transition smooth any sub-window
// jitter. This leading-edge coalescer, keyed per queryKey, collapses a burst
// into: one apply immediately, then one folded apply per `APPLY_COALESCE_MS`
// while events keep arriving, plus a trailing flush so the final state is
// never missed. EVERY event is still applied — only the notify is batched;
// the fold `reduce(applyFn)` is order-preserving and correct for both `set`
// (last-wins) and `add` (D6-accumulate) recipes.
//
// Devtool semantics: `cacheWrites.byNats` now counts coalesced WRITES
// (flushes), not raw events; `dispatch.total` still counts every event (the
// sequence gate runs per-event upstream in `applyNow`). See `DEFERRED.md`.
// A shared scheduler drains at most eight independent query-key flushes per
// macrotask. Each key's fold remains one atomic cache write, while a broad
// update burst yields between groups instead of monopolising the JS thread.
// ─────────────────────────────────────────────────────────────────────────
/** Coalesce window for applyEvent cache patches — ~one frame past a burst. */
const APPLY_COALESCE_MS = 100;
const APPLY_SLICE_TASKS = 8;
export function runApplyTasksInSlices(tasks, onComplete, schedule = task => {
  setTimeout(task, 0);
}) {
  const applyNext = () => {
    const slice = tasks.splice(0, APPLY_SLICE_TASKS);
    for (const task of slice) {
      task();
    }
    if (tasks.length > 0) {
      schedule(applyNext);
    } else {
      onComplete();
    }
  };
  applyNext();
}
const _applyFlushTasks = [];
const _scheduledApplyTasks = new Map();
let _applyFlushScheduled = false;
function runScheduledApplyTask(task) {
  if (_scheduledApplyTasks.get(task.queryHash) !== task) return;
  _scheduledApplyTasks.delete(task.queryHash);
  task.run();
}
function drainScheduledApplyTask(queryHash) {
  const task = _scheduledApplyTasks.get(queryHash);
  if (task !== undefined) runScheduledApplyTask(task);
}
function scheduleApplyFlush(queryHash, run) {
  const task = {
    queryHash,
    run
  };
  _scheduledApplyTasks.set(queryHash, task);
  _applyFlushTasks.push(() => runScheduledApplyTask(task));
  if (_applyFlushScheduled) return;
  _applyFlushScheduled = true;
  setTimeout(() => {
    runApplyTasksInSlices(_applyFlushTasks, () => {
      _applyFlushScheduled = false;
    });
  }, 0);
}
const _applyCoalesce = new Map();
function eventPayloadBytes(event) {
  const data = event?.data;
  return data instanceof Uint8Array ? data.byteLength : 0;
}
/**
 * Leading-edge coalescer around the `applyEvent` cache patch, keyed per
 * queryKey. First event of a burst applies immediately (the feed stays
 * instant); events inside the window buffer and are folded into ONE
 * `setQueryData` when the window closes. A non-empty buffer re-arms the
 * window; an empty one drops the entry so the next event after a quiet
 * period is a fresh leading edge again.
 */
/** Row count of a paged cache value, or -1 when the shape has no `.data`
 *  array. Used to detect a realtime INSERT (count grew) for the
 *  reconcile-on-insert path — a `set`/`add` patch never changes the count. */
function pagedRowCount(value) {
  const data = value?.data;
  return Array.isArray(data) ? data.length : -1;
}
/**
 * Reconcile-on-insert (event-as-row `prepend: { reconcile: true }`). When a
 * realtime write GROWS the list — a new row was inserted by the prepend
 * recipe — the synthesized row is a PARTIAL placeholder (fields absent from
 * the wire are null/estimated). Fire ONE throttled REST refetch through the
 * parent observer (the canonical realtime refetch path, same as the
 * onSignal:invalidate branch) so the placeholder reconciles to the
 * authoritative server row. set/add patches and replace-in-place don't grow
 * the list, so they never refetch; the per-queryKey throttle (≥2s) bounds it
 * under sustained trade flow.
 */
/**
 * Pure decision for {@link maybeReconcileOnInsert} — true iff the hook opted
 * into reconcile-on-insert AND the realtime write GREW the paged list (a new
 * row was inserted by an event-as-row prepend). A `set`/`add` patch or a
 * replace-in-place keeps the row count, so it returns false; a non-paged shape
 * (`-1` count both sides) also returns false. Exported for unit testing.
 */
export function shouldReconcileOnInsert(reconcileOnInsert, prev, next) {
  if (reconcileOnInsert !== true) return false;
  return pagedRowCount(next) > pagedRowCount(prev);
}
function maybeReconcileOnInsert(qc, key, reconcileOnInsert, prev, next) {
  if (!shouldReconcileOnInsert(reconcileOnInsert, prev, next)) return;
  void qc.invalidateQueries({
    queryKey: key,
    refetchType: 'none'
  });
  throttledRealtimeRefetch(qc, key);
}
function bufferedEventCoveredBySnapshot(cached, item, queryHash, resolveSnapshotSequence) {
  if (item.sequenceId === undefined) return false;
  const event = item.ev?.data;
  const custom = resolveSnapshotSequence?.(cached, item.subject, event);
  const snapshot = custom === undefined || custom.kind === 'unhandled' ? scopedSnapshotSequence(cached, item.subject, event) : custom;
  return snapshotCoversCursor(snapshot, queryHash, item.sequenceId);
}
export function coalescedApplyEvent(qc, key, wrapped, applyFn, specName, rowKey, reconcileOnInsert, immediate, resolveSnapshotSequence) {
  const h = hashKey(key);
  if (immediate) {
    // Preserve per-key write order across the scheduler boundary. A terminal
    // event must observe every older buffered update and then win.
    drainScheduledApplyTask(h);
    const pending = _applyCoalesce.get(h);
    if (pending !== undefined && pending.buffer.length > 0) pending.flush(false);
  }
  if (!immediate) {
    const existing = _applyCoalesce.get(h);
    if (existing !== undefined && (existing.timer !== null || _scheduledApplyTasks.has(h))) {
      // Inside an open window — buffer for the trailing flush.
      const payloadBytes = eventPayloadBytes(wrapped);
      existing.buffer.push({
        subject: wrapped.subject ?? '<unknown>',
        ev: wrapped,
        payloadBytes,
        queuedAt: performance.now(),
        sequenceId: wrapped.sequenceId
      });
      if (process.env.NODE_ENV !== 'production') {
        _enqueueApplyPerf(payloadBytes);
      }
      return;
    }
  }
  // Leading edge — apply immediately so a lone event feels instant. A throw
  // here propagates to `_runDispatchBranch`'s catch (the live-event error
  // path); buffered-event errors are caught in `flush` below.
  if (process.env.NODE_ENV !== 'production') {
    markNextWrite(qc, h, 'nats');
  }
  const trackDiff = rowKey !== undefined || reconcileOnInsert === true;
  const prevBefore = trackDiff ? qc.getQueryData(key) : undefined;
  qc.setQueryData(key, prev => applyFn(prev, wrapped));
  if (trackDiff) {
    const nextAfter = qc.getQueryData(key);
    if (rowKey !== undefined) {
      tagNewRowsFromDiff(qc, h, rowKey, prevBefore, nextAfter);
    }
    maybeReconcileOnInsert(qc, key, reconcileOnInsert, prevBefore, nextAfter);
  }
  if (immediate) return;
  const applyBatch = batch => {
    const cur = _applyCoalesce.get(h);
    if (cur === undefined || cur.flush !== flush) return;
    const flushStartedAt = process.env.NODE_ENV !== 'production' ? performance.now() : 0;
    if (process.env.NODE_ENV !== 'production') {
      const batchBytes = batch.reduce((sum, item) => sum + item.payloadBytes, 0);
      _flushApplyPerf(batch.length, batchBytes);
      _observe('applyBatchEvents', batch.length);
      const oldest = batch[0];
      if (oldest !== undefined) {
        _observe('applyOldestEventAgeMs', flushStartedAt - oldest.queuedAt);
      }
      markNextWrite(qc, h, 'nats');
    }
    const prevBeforeFlush = trackDiff ? qc.getQueryData(key) : undefined;
    qc.setQueryData(key, prev => {
      let acc = prev;
      for (const item of batch) {
        if (bufferedEventCoveredBySnapshot(acc, item, h, resolveSnapshotSequence)) {
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('dispatch.seqGateSkipped');
          }
          continue;
        }
        try {
          acc = cur.applyFn(acc, item.ev);
        } catch (err) {
          // applyEvent:error is a PRODUCTION telemetry channel.
          sdkEvents.emit('applyEvent:error', {
            specName: cur.specName,
            subject: item.subject,
            queryKey: key,
            error: err instanceof Error ? err.message : String(err)
          });
        }
      }
      return acc;
    });
    if (trackDiff) {
      const nextAfter = qc.getQueryData(key);
      if (rowKey !== undefined) {
        tagNewRowsFromDiff(qc, h, rowKey, prevBeforeFlush, nextAfter);
      }
      maybeReconcileOnInsert(qc, key, reconcileOnInsert, prevBeforeFlush, nextAfter);
    }
    if (process.env.NODE_ENV !== 'production') {
      _observe('applyFlushDurationMs', performance.now() - flushStartedAt);
    }
    cur.timer = setTimeout(() => flush(true), APPLY_COALESCE_MS);
  };
  const flush = defer => {
    const cur = _applyCoalesce.get(h);
    if (cur === undefined || cur.flush !== flush) return;
    if (cur.timer !== null) clearTimeout(cur.timer);
    cur.timer = null;
    if (cur.buffer.length === 0) {
      _applyCoalesce.delete(h);
      return;
    }
    const batch = cur.buffer;
    cur.buffer = [];
    if (defer) {
      scheduleApplyFlush(h, () => applyBatch(batch));
    } else {
      applyBatch(batch);
    }
  };
  const entry = {
    timer: null,
    buffer: [],
    applyFn: applyFn,
    specName,
    flush
  };
  entry.timer = setTimeout(() => flush(true), APPLY_COALESCE_MS);
  _applyCoalesce.set(h, entry);
}
// ─────────────────────────────────────────────────────────────────────────
// Per-row viewport gate — row-id extraction.
//
// A row-keyed list hook (`useGetMarkets`) dispatches every market's events
// through one whole-list reducer on always-on domain-wildcard pins. To avoid
// re-rendering a card scrolled offscreen, `applyNow` extracts the row id from
// the concrete event subject and consults the registry's viewport tracker;
// offscreen rows BUFFER their events (replayed on scroll-in) instead of
// patching the cache. `add` recipes accumulate, so events are never dropped —
// only deferred — and a row offscreen past the buffer cap falls back to a
// refetch.
// ─────────────────────────────────────────────────────────────────────────
/** Per-row offscreen-event buffer cap. Past this, the row falls back to a
 *  refetch on scroll-in (the server returns true cumulative `add` values). */
const ROW_BUFFER_CAP = 30;
/**
 * Extract the row id from a concrete event subject using the hook's
 * `rowSubjectTemplates`. A template carries the row dimension as a `{…}`
 * placeholder; literal `*` segments are NATS single-token wildcards. Returns
 * the captured id, or `null` when no template matches (e.g. a broadcast
 * subject like `morfi.market.created.v0` — no row dimension, never gated).
 *
 * ONLY the `{rowKey}` placeholder is captured as the row id. The per-row
 * viewport gate compares that id against the registry's per-ENTITY visibility,
 * and a list row registers its visibility under its `rowKey` value
 * (`useSdkRow`'s `<Row id={rowKey}>` → `acquireWhenVisible(..., entityId=id)`).
 *
 * A `rowField:`-bound template names a DIFFERENT dimension than the rowKey —
 * positions gate their wire subject on `{market}` / `{outcomeMint}` while their
 * rowKey is `positionId`. Capturing that field value would make the gate test a
 * foreign entity namespace: the markets feed registers `morfi.market.<pubkey>`
 * visibility under the bare market pubkey, those entries linger at refcount 0
 * (hidden) after blur, and the positions gate would then read
 * `isEntityVisible(<market>) === false` and buffer every peer trade forever —
 * the parked-portfolio freeze. Field-bound placeholders therefore match any
 * token but are NOT captured; with no `{rowKey}` segment the subject yields
 * `null` and the gate correctly no-ops (the row is identified by its own
 * always-visible registration, not by the subject's market dimension).
 */
export function extractRowId(templates, subject, rowKey) {
  const subjSegs = subject.split('.');
  const rowKeyPlaceholder = rowKey !== undefined ? `{${rowKey}}` : undefined;
  for (const tmpl of templates) {
    const tmplSegs = tmpl.split('.');
    if (tmplSegs.length !== subjSegs.length) continue;
    let rowId = null;
    let matched = true;
    for (let i = 0; i < tmplSegs.length; i += 1) {
      const t = tmplSegs[i];
      const s = subjSegs[i];
      if (t.length > 1 && t.startsWith('{') && t.endsWith('}')) {
        // Placeholder: matches any single token. Capture only when it is the
        // rowKey — a field-bound placeholder is a different namespace (see JSDoc).
        if (t === rowKeyPlaceholder) rowId = s;
      } else if (t !== '*' && t !== s) {
        matched = false;
        break;
      }
    }
    if (matched && rowId !== null) return rowId;
  }
  return null;
}
/**
 * Derive the SEQUENCE-GATE key subject for a concrete event subject.
 *
 * The sequence gate is a per-ordered-substream high-water mark: JetStream
 * delivers events IN ORDER within one consumer, so within a gate key the
 * only non-monotonic arrivals are duplicates (see `seq-gate-store.ts`). That
 * invariant only holds when the gate key binds ONE substream. A list hook
 * whose `perEntitySubjects` is a MULTI-ENTITY wildcard (e.g.
 * `morfi.amm.*.user.*.activity.v0`, used by `useGetWinners`) would
 * otherwise gate on that wildcard pattern and share ONE high-water across
 * EVERY entity's events. Because the chain cursor is globally monotonic, a
 * busy sibling entity (or a reconnect replay) advances the shared mark past
 * THIS entity's next event, and `admitEvent` then wrongly drops it — the
 * row's cache freezes while other rows tick (the winner-page market-cap
 * freeze).
 *
 * Fix: when a `rowSubjectTemplate` matches the concrete subject, key the gate
 * per ENTITY by substituting the concrete entity token into the template's
 * `{…}` placeholder while keeping literal `*` segments (the actor routing
 * key) as `*`. So each pool/market advances its OWN cursor. Concrete-subject
 * hooks (the chart, single detail hooks) already match a placeholder-free
 * template → the result equals the matched pattern → behaviour unchanged.
 * Returns `fallback` (the matched pattern) for broadcast subjects that match
 * no row template.
 */
export function gateSubjectForTemplates(templates, subject, fallback) {
  const subjSegs = subject.split('.');
  for (const tmpl of templates) {
    const tmplSegs = tmpl.split('.');
    if (tmplSegs.length !== subjSegs.length) continue;
    const out = [];
    let matched = true;
    for (let i = 0; i < tmplSegs.length; i += 1) {
      const t = tmplSegs[i];
      const s = subjSegs[i];
      if (t.length > 1 && t.startsWith('{') && t.endsWith('}')) {
        out.push(s);
      } else if (t === '*') {
        out.push('*');
      } else if (t === s) {
        out.push(t);
      } else {
        matched = false;
        break;
      }
    }
    if (matched) return out.join('.');
  }
  return fallback;
}
/**
 * True when `subject` matches ANY pattern in `patterns` segment-wise, treating
 * a `*` segment as a single-token wildcard (literal segments must match
 * exactly). Used by the `reconcileAlways` own-trade refetch (MOR-923): the
 * patterns are the viewer's OWN concrete subjects, so a match means the viewer
 * just traded and the query must refetch to relearn structural state (balance /
 * cost basis) the wire does not carry. Exported for unit testing.
 */
export function subjectMatchesAny(patterns, subject) {
  if (patterns.length === 0) return false;
  const segs = subject.split('.');
  for (const pattern of patterns) {
    const pSegs = pattern.split('.');
    if (pSegs.length !== segs.length) continue;
    let matched = true;
    for (let i = 0; i < pSegs.length; i += 1) {
      const p = pSegs[i];
      if (p === '*') continue;
      if (p !== segs[i]) {
        matched = false;
        break;
      }
    }
    if (matched) return true;
  }
  return false;
}
function _runDispatchBranch(qc, key, wrapped, applyFn, signalFn, specName, rowKey, reconcileOnInsert, immediateApply, resolveSnapshotSequence) {
  // Stage-simulator freeze (devtool). A query with injected debug data has
  // its hash registered in the debug-override registry; while registered we
  // skip every cache mutation (applyEvent / invalidate) so a real chain event
  // cannot revert the injected fixture. Empty Set in production — see
  // `debug-override-registry.ts`. Reported as a benign no-op outcome.
  if (isDebugOverridden(hashKey(key))) {
    return {
      branch: 'onSignal:noop',
      error: null
    };
  }
  // Dispatch precedence (spec 05 §499-516):
  //   applyEvent > onSignal > pure-invalidate fallback.
  //
  // `key` is `readonly unknown[]` (TanStack's `QueryKey` is the same shape).
  // We pass it through directly — `setQueryData` / `invalidateQueries` do
  // not mutate the key, and skipping the per-dispatch `[...key]` spread
  // removes one array allocation + GC entry per NATS event (was ~500
  // allocs/sec under sustained trade flow before this fix).
  if (applyFn !== undefined) {
    try {
      // Coalesced cache patch — leading-edge apply + per-window folded
      // flush (see `coalescedApplyEvent`). `markNextWrite` / `setQueryData`
      // both happen inside the coalescer so the devtool `cache:write`
      // marker tracks the actual (possibly batched) write.
      coalescedApplyEvent(qc, key, wrapped, applyFn, specName, rowKey, reconcileOnInsert, immediateApply, resolveSnapshotSequence);
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('dispatch.total');
        _bumpPerf('dispatch.byBranch.applyEvent');
      }
      return {
        branch: 'applyEvent',
        error: null
      };
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('dispatch.total');
        _bumpPerf('dispatch.byBranch.applyEvent');
        _bumpPerf('dispatch.errors');
      }
      return {
        branch: 'applyEvent',
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }
  if (signalFn !== undefined) {
    try {
      if (signalFn(wrapped)) {
        // Mark the query stale (so a later mount/focus also refetches),
        // then fire a REAL HTTP refetch via `refetchRealtimeQuery`.
        // `invalidateQueries`'s own refetch goes through `Query.fetch()`
        // with the latched `queryFn` — which for a Morfi realtime query
        // is typically the subscriber sentinel (last-setOptions-wins),
        // so it resolves with stale cached data and NO HTTP fires. With
        // `refetchType: 'none'` we only flip the stale flag here and let
        // `refetchRealtimeQuery` (which refetches through the parent
        // data-owning observer) do the actual network fetch — the
        // canonical realtime refetch path. See `refetchRealtimeQuery.ts`.
        // The refetch is throttled per queryKey (`throttledRealtimeRefetch`)
        // so a high-frequency channel can't storm `GET`s; the stale flag
        // below is still flipped per-event (cheap, no network).
        void qc.invalidateQueries({
          queryKey: key,
          refetchType: 'none'
        });
        throttledRealtimeRefetch(qc, key);
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('dispatch.total');
          _bumpPerf('dispatch.byBranch.onSignalInvalidate');
        }
        return {
          branch: 'onSignal:invalidate',
          error: null
        };
      }
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('dispatch.total');
        _bumpPerf('dispatch.byBranch.onSignalNoop');
      }
      return {
        branch: 'onSignal:noop',
        error: null
      };
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('dispatch.total');
        _bumpPerf('dispatch.byBranch.onSignalInvalidate');
        _bumpPerf('dispatch.errors');
      }
      return {
        branch: 'onSignal:invalidate',
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }
  // Pure-subscription fallback — no patch, no gate. Same realtime-refetch
  // contract as the `onSignal:invalidate` branch above: flip the stale
  // flag without `invalidateQueries`' sentinel-bound refetch, then fire a
  // throttled real HTTP refetch through the parent observer.
  void qc.invalidateQueries({
    queryKey: key,
    refetchType: 'none'
  });
  throttledRealtimeRefetch(qc, key);
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('dispatch.total');
    _bumpPerf('dispatch.byBranch.invalidate');
  }
  return {
    branch: 'invalidate',
    error: null
  };
}
function skipAutomaticAuthoritativeRefetchOnMount() {
  return false;
}
/**
 * Build the canonical realtime hook options. EVERY codegen-emitted
 * `useGet*` realtime hook composes through this helper — bare
 * `useQuery({queryKey, queryFn})` is banned (source-grep rule
 * FQ-REALTIME-OPTS-001).
 *
 * @param spec         · the spec-emitted queryKey + queryFn.
 * @param connectivity · the module-level connectivity-store hook
 *                        (provides `lastReconnectAt` as the staleness
 *                        gate for the function-form `refetchOnMount`).
 */
export function realtimeQueryOptions(spec, connectivity, refetchInterval, fetchOnce = false) {
  return {
    queryKey: [...spec.queryKey],
    queryFn: spec.queryFn,
    // Preserve codegen-emitted meta (e.g. `meta.realtime: true`) so the
    // connectivity ladder's `q.meta?.realtime === true` predicate matches.
    ...(spec.meta !== undefined ? {
      meta: spec.meta
    } : {}),
    // (1) L1296 retryOnMount — failed prefetch on server must not auto-retry on client
    retryOnMount: false,
    // (2) L1296 staleTime — NATS, not the clock, drives invalidation
    staleTime: Number.POSITIVE_INFINITY,
    // (4) keepPreviousData — when the args (and therefore the queryKey)
    // change, hold the PREVIOUS key's data on the observer until the new
    // key's fetch settles. Without this, every filter/sort switch on a
    // list surface flips `isPending` true (the new key has no cache entry)
    // and the consumer page renders a full-page skeleton mid-interaction.
    // `placeholderData` is observer-local — it is NOT written into the
    // QueryCache — so it never pollutes a sibling observer's data and the
    // real fetch result still lands in the cache normally.
    placeholderData: (previousData, previousQuery) => keepPreviousDataAcrossQueryKeys(spec.queryKey, previousData, previousQuery),
    // (3) L1296 refetchOnMount — FUNCTION form; literal 'always' is BANNED.
    refetchOnMount: query => !fetchOnce && query.state.dataUpdatedAt < connectivity.lastReconnectAt,
    // Connectivity-store drives reconnect re-sync via lastReconnectAt;
    // the TanStack default refetchOnReconnect:true would race with that.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // NATS-drop → REST-poll fallback. Function-form per TanStack docs:
    // "If set to a function, the function will be executed with the
    //  latest data and query to compute a frequency."
    // (https://tanstack.com/query/latest/docs/framework/react/reference/useQuery)
    // Returns `false` when CONNECTED → no polling.
    // Returns the per-query ladder interval (4 s → 8 s → 16 s → 32 s)
    // when DEGRADED_NO_NATS. Re-evaluation is triggered by
    // `useRealtimeQuery` subscribing to `connectivityStore.state` so
    // every state transition causes a hook re-render → observer
    // `setOptions` → function-form re-read.
    refetchInterval: fetchOnce ? false : refetchInterval ?? (query => degradedPollInterval(query)),
    ...(fetchOnce ? {
      retry: false
    } : {})
  };
}
export function useRealtimeQuery(opts) {
  const qc = useQueryClient();
  const registry = useContext(RegistryContext);
  // T2003 · `boundRef` MUST be declared at the hook's top level so the
  // `setVisibilityRef` callback (declared below) can flip it. Declaring
  // inside `if (opts.viewportGated)` would scope it out of the callback
  // closure that needs to mutate it.
  const boundRef = useRef(false);
  // ─── Latest-in-ref pattern (Compiler-clean) ────────────────────────
  // Each ref tracks the latest opts.* value, written in `useEffect` so we
  // never mutate refs during render (React Compiler rejects render-time
  // ref writes — see https://react.dev/reference/react/useRef). The
  // dispatchEvent callback reads via `.current` whenever a NATS event
  // arrives — which is always AFTER the post-commit effect has run, so
  // refs are guaranteed up-to-date by the time a dispatch happens.
  const applyEventRef = useRef(opts.applyEvent);
  const queryFnRef = useRef(opts.queryFn);
  const validateEventRef = useRef(opts.validateEvent);
  const requiresAuthoritativeRefetchRef = useRef(opts.requiresAuthoritativeRefetch);
  const applyImmediatelyRef = useRef(opts.applyImmediately === true);
  const admitSequenceOnlyOnMutationRef = useRef(opts.admitSequenceOnlyOnMutation === true);
  const sequenceGateSubjectRef = useRef(opts.sequenceGateSubject);
  const queryKeyRef = useRef(opts.queryKey);
  // T15002 · signal-path closure (boolean gate).
  const onSignalRef = useRef(opts.onSignal);
  // Async side-effect closure — fires in parallel with applyEvent.
  // Codegen wires this from `prepend:` manifest blocks; see
  // `_<HookName>OnEvent` for the per-recipe implementation.
  const onEventRef = useRef(opts.onEvent);
  // `specName` is per-instance stable but the compiler can't prove that,
  // so route it through a ref to keep the `dispatchEvent` useCallback's
  // memoization preserved (no closure over the live `opts` object).
  const specNameRef = useRef(opts.specName);
  // Subject PATTERNS this hook is subscribed to (perEntity + domainPins).
  // `dispatchEvent` is an empty-deps useCallback, so it reads the live
  // pattern set through this ref to recover which pattern a concrete
  // event matched — the sequence gate keys per (queryKey, subjectPattern).
  const gatePatternsRef = useRef([]);
  // Phase 2 · subscribe-before-snapshot buffering. Until the first REST
  // snapshot resolves there is no cached row for an event to patch
  // (`applyEvent(prev=undefined)` bails), so events are buffered here and
  // replayed by `flushBuffer` once the snapshot lands.
  const bufferRef = useRef([]);
  const bufferOverflowRef = useRef(false);
  const snapshotLandedRef = useRef(false);
  // Live-since-mount fence — captured at snapshot-landed time so this hook
  // instance's `liveSinceMountIds` projection only includes rowKeys whose
  // `tagLiveRow` seq strictly exceeds this snapshot. `null` until the
  // snapshot resolves. Per-mount via `useState` — a remount creates fresh
  // state even when the cache is shared. The fence is exposed via a sidecar
  // `WeakMap` (`attachFenceToResult` / `readFenceFromResult` in
  // `live-since-mount-store`) rather than as a property on the query
  // result — see `attachFenceToResult` for the spread/tracking rationale.
  // `useState` triggers exactly one re-render when the fence transitions
  // from `null` to a number, so `createListHook`'s `liveSinceMountIds`
  // getter picks up the freshly-meaningful projection.
  const [liveSinceMountFenceSeq, setLiveSinceMountFenceSeq] = useState(null);
  const queryScopeHash = hashKey(opts.queryKey);
  const subscribeFetchOncePolicy = useCallback(listener => subscribeFetchOnceRealtimePolicy(qc, listener), [qc]);
  const readFetchOnceOwnerActive = useCallback(() => hasActiveFetchOnceRealtimeOwner(qc, opts.queryKey), [opts.queryKey, qc]);
  const fetchOnceOwnerActive = React.useSyncExternalStore(subscribeFetchOncePolicy, readFetchOnceOwnerActive, () => false);
  // SDKProvider increments this before swapping a NATS client or its
  // authorized roots, and after a REST-only give-up once a late wire lands.
  // A previously completed handoff cannot cover the next consumer, so it
  // gets a fresh REST fence even when the query key is the same. Subscribed
  // (not just read) so a wire that arrives after this hook mounted re-runs
  // its handoff without waiting for an unrelated re-render.
  const subscribeWireInterestVersion = useCallback(listener_0 => registry?.subscribeWireInterestVersion?.(listener_0) ?? (() => {}), [registry]);
  const readWireInterestVersion = useCallback(() => registry?.getWireInterestVersion?.() ?? 0, [registry]);
  const wireInterestVersion = React.useSyncExternalStore(subscribeWireInterestVersion, readWireInterestVersion, readWireInterestVersion);
  const handoffScopeHash = `${queryScopeHash}:wire:${wireInterestVersion}`;
  // Per-row viewport gate state. `rowSubjectTemplatesRef` mirrors the latest
  // `opts.rowSubjectTemplates`; `rowBufferRef` holds offscreen rows' deferred
  // events keyed by row id; `staleRowsRef` flags rows that overflowed the
  // buffer cap and must refetch on scroll-in instead of replaying.
  const rowSubjectTemplatesRef = useRef(opts.rowSubjectTemplates ?? []);
  const isTerminalEventRef = useRef(opts.isTerminalEvent);
  // Codegen emits `rowKey: 'signature' as const` — value-stable across renders,
  // but the empty-deps `applyNow` callback must read via a ref to satisfy
  // useExhaustiveDependencies AND to stay identity-stable for the registry.
  const rowKeyRef = useRef(opts.rowKey);
  const rowBufferRef = useRef(new Map());
  const staleRowsRef = useRef(new Set());
  useRealtimeBufferResourceReader(rowBufferRef, staleRowsRef, bufferRef);
  // Per-QUERY viewport gate — `opts.viewportEntity` is the single market the
  // whole query belongs to (arg-bound list hooks). Buffered events reuse
  // `rowBufferRef` keyed by this entity, so the existing `onEntityVisible`
  // flush + cache-subscription buffer-clear cover it with no extra plumbing.
  const viewportEntityRef = useRef(opts.viewportEntity);
  // Reconcile-on-insert flag (event-as-row `prepend: { reconcile: true }`).
  const reconcileOnInsertRef = useRef(opts.reconcileOnInsert);
  // Reconcile-always subjects (own-trade structural reconcile, MOR-923) —
  // concrete subjects that trigger a throttled refetch on every match.
  const reconcileAlwaysSubjectsRef = useRef(opts.reconcileAlwaysSubjects ?? []);
  const snapshotSequenceRef = useRef(opts.snapshotSequence);
  const requireEventSequenceRef = useRef(opts.requireEventSequence === true);
  const authoritativeRefetchRef = useRef(null);
  useEffect(() => {
    applyEventRef.current = opts.applyEvent;
    queryFnRef.current = opts.queryFn;
    validateEventRef.current = opts.validateEvent;
    requiresAuthoritativeRefetchRef.current = opts.requiresAuthoritativeRefetch;
    applyImmediatelyRef.current = opts.applyImmediately === true;
    admitSequenceOnlyOnMutationRef.current = opts.admitSequenceOnlyOnMutation === true;
    sequenceGateSubjectRef.current = opts.sequenceGateSubject;
    queryKeyRef.current = opts.queryKey;
    onSignalRef.current = opts.onSignal;
    onEventRef.current = opts.onEvent;
    specNameRef.current = opts.specName;
    rowSubjectTemplatesRef.current = opts.rowSubjectTemplates ?? [];
    isTerminalEventRef.current = opts.isTerminalEvent;
    rowKeyRef.current = opts.rowKey;
    viewportEntityRef.current = opts.viewportEntity;
    reconcileOnInsertRef.current = opts.reconcileOnInsert;
    reconcileAlwaysSubjectsRef.current = opts.reconcileAlwaysSubjects ?? [];
    snapshotSequenceRef.current = opts.snapshotSequence;
    requireEventSequenceRef.current = opts.requireEventSequence === true;
  });
  useEffect(() => {
    // A wire replacement needs a new snapshot boundary even when the REST
    // query key remains unchanged.
    void handoffScopeHash;
    if (hashKey(queryKeyRef.current) !== queryScopeHash) return;
    if (restOnlyScopeRef.current !== null && restOnlyScopeRef.current !== handoffScopeHash) {
      restOnlyScopeRef.current = null;
      wireHandoffAttemptRef.current = null;
    }
    snapshotLandedRef.current = false;
    bufferRef.current = [];
    bufferOverflowRef.current = false;
    rowBufferRef.current.clear();
    staleRowsRef.current.clear();
    setLiveSinceMountFenceSeq(null);
  }, [handoffScopeHash, queryScopeHash]);
  const startAuthoritativeRefetch = useCallback((queryKey, subject, event, requiredCursor) => {
    const queryHash = hashKey(queryKey);
    const active = authoritativeRefetchRef.current;
    if (active?.queryHash === queryHash) {
      if (compareCursor(requiredCursor, active.requiredCursor) > 0) {
        active.subject = subject;
        active.event = event;
        active.requiredCursor = requiredCursor;
        active.attempts = 0;
      }
      return;
    }
    const previous = active;
    if (previous?.timer !== null && previous?.timer !== undefined) {
      clearTimeout(previous.timer);
    }
    const state = {
      queryHash,
      queryKey: [...queryKey],
      subject,
      event,
      requiredCursor,
      attempts: 0,
      timer: null
    };
    authoritativeRefetchRef.current = state;
    const run = async () => {
      if (authoritativeRefetchRef.current !== state) return;
      state.attempts += 1;
      let outcome;
      try {
        outcome = await refetchRealtimeQuery(qc, state.queryKey, queryFnRef.current);
      } catch {
        outcome = 'error';
      }
      if (authoritativeRefetchRef.current !== state) return;
      const cached = qc.getQueryData(state.queryKey);
      const custom = snapshotSequenceRef.current?.(cached, state.subject, state.event);
      const snapshotSequence = custom === undefined || custom.kind === 'unhandled' ? scopedSnapshotSequence(cached, state.subject, state.event) : custom;
      const snapshotCoversEvent = snapshotCoversCursor(snapshotSequence, state.queryHash, state.requiredCursor);
      if (outcome === 'success' && snapshotCoversEvent) {
        authoritativeRefetchRef.current = null;
        return;
      }
      if (outcome === 'no_observer') {
        authoritativeRefetchRef.current = null;
        return;
      }
      void qc.invalidateQueries({
        queryKey: state.queryKey,
        refetchType: 'none'
      });
      const delayIndex = Math.min(state.attempts - 1, AUTHORITATIVE_REFETCH_RETRY_MS.length - 1);
      const delayMs = AUTHORITATIVE_REFETCH_RETRY_MS[delayIndex];
      state.timer = setTimeout(() => {
        state.timer = null;
        void run();
      }, delayMs);
    };
    void run();
  }, [qc]);
  useEffect(() => {
    const mountedScopeHash = queryScopeHash;
    return () => {
      const state_0 = authoritativeRefetchRef.current;
      if (state_0?.queryHash !== mountedScopeHash) return;
      if (state_0.timer !== null) clearTimeout(state_0.timer);
      authoritativeRefetchRef.current = null;
    };
  }, [queryScopeHash]);
  // The actual apply path — sequence gate + cache patch. `applyNow` runs
  // for live events AND for buffered events replayed after the snapshot
  // lands. Empty-ish deps (`[qc]`) → stable identity; reads via refs.
  const applyNow = useCallback((subject_0, ev) => {
    const applyFn = applyEventRef.current;
    const signalFn = onSignalRef.current;
    const key = queryKeyRef.current;
    // Wrap the raw bytes / decoded payload from the registry into the
    // shape codegen-emitted applyEvent closures expect:
    //   `{ subject: <pattern that fired>, data: <raw bytes-or-object> }`.
    // The registry erases the wrapper at the trie boundary; we re-attach
    // `subject` here so a recipe can branch between subjects (e.g.
    // `morfi.market.X.trade` vs `morfi.market.X.state_change`).
    // Codegen's per-subject decode reads the wire bytes from this
    // `.data` field (`fromBinary(<Msg>Schema, ev.data)`).
    const wrapped = {
      subject: subject_0,
      data: ev
    };
    // ─── Sequence gate (Binance-order-book pattern) ───────────────────
    // Drop stale / re-delivered events BEFORE they patch the cache.
    // JetStream is at-least-once and reconnect-replay re-delivers events
    // around the persisted anchor; ungated, an `add` reducer
    // double-counts and a `set` reducer stale-overwrites. The event's
    // chain cursor (`sequenceId` — protobuf field 1) is compared against
    // a per-(queryKey, subjectPattern) high-water mark — see
    // `src/nats/seq-gate-store.ts`. Fail-open: an event whose cursor
    // can't be decoded is applied ungated (pre-gate behaviour).
    let sequenceId;
    let pendingGate;
    if (ev instanceof Uint8Array) {
      const cursor = extractSequenceId(ev);
      if (cursor !== null && cursor.length === SEQUENCE_ID_LEN) {
        sequenceId = cursor;
        const patterns = gatePatternsRef.current;
        let pattern = subject_0;
        for (let i = 0; i < patterns.length; i++) {
          const p = patterns[i];
          if (matchSubject(p, subject_0)) {
            pattern = p;
            break;
          }
        }
        // Gate per ORDERED SUBSTREAM, not per wildcard pattern. A list hook
        // bound to a multi-entity wildcard (`morfi.amm.*.…`) would otherwise
        // share ONE high-water across every entity, so a busy sibling entity
        // / reconnect replay advances the mark past this entity's next event
        // and `admitEvent` drops it (the row's cache freezes). Concretize the
        // entity from the matching `rowSubjectTemplate` so each entity gets
        // its own cursor; keep the literal actor `*`. Concrete-subject hooks
        // are unaffected (their template has no `{…}` → result == pattern).
        const baseGateSubject = gateSubjectForTemplates(rowSubjectTemplatesRef.current, subject_0, pattern);
        const gateSubject = sequenceGateSubjectRef.current?.(baseGateSubject, ev) ?? baseGateSubject;
        const queryHash_0 = hashKey(key);
        const gk = gateKey(queryHash_0, gateSubject);
        // Re-seed monotonically on every event. An authoritative REST
        // reconciliation may have advanced while this hook remained mounted;
        // lazy-only seeding would leave the old gate behind and reapply stale
        // NATS events already reflected in that newer snapshot.
        const cached_0 = qc.getQueryData(key);
        const custom_0 = snapshotSequenceRef.current?.(cached_0, subject_0, ev);
        const scoped = custom_0 === undefined || custom_0.kind === 'unhandled' ? scopedSnapshotSequence(cached_0, subject_0, ev) : custom_0;
        if (scoped.kind === 'cursor') {
          seedGate(gk, scoped.cursor);
        } else if (scoped.kind === 'invalid') {
          sdkEvents.emit('applyEvent:error', {
            specName: specNameRef.current,
            subject: subject_0,
            queryKey: key,
            error: 'realtime snapshot sequence is malformed'
          });
          throttledRealtimeRefetch(qc, key);
          return;
        } else if (scoped.kind === 'unhandled') {
          const seed = base64ToCursor(readSnapshotLastSeq(queryHash_0));
          if (seed !== null) seedGate(gk, seed);
        }
        if (!wouldAdmitEvent(gk, cursor)) {
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('dispatch.total');
            _bumpPerf('dispatch.seqGateSkipped');
          }
          return;
        }
        pendingGate = {
          key: gk,
          cursor
        };
      }
    }
    if (sequenceId === undefined && requireEventSequenceRef.current) {
      sdkEvents.emit('applyEvent:error', {
        specName: specNameRef.current,
        subject: subject_0,
        queryKey: key,
        error: 'realtime event sequence is missing or malformed'
      });
      throttledRealtimeRefetch(qc, key);
      return;
    }
    if (sequenceId !== undefined) {
      wrapped.sequenceId = sequenceId;
    }
    const validationError = validateEventRef.current?.(qc.getQueryData(key), wrapped);
    if (validationError !== undefined && validationError !== null) {
      sdkEvents.emit('applyEvent:error', {
        specName: specNameRef.current,
        subject: subject_0,
        queryKey: key,
        error: validationError
      });
      void qc.invalidateQueries({
        queryKey: key,
        refetchType: 'none'
      });
      throttledRealtimeRefetch(qc, key);
      return;
    }
    if (requiresAuthoritativeRefetchRef.current?.(qc.getQueryData(key), wrapped) === true) {
      void qc.invalidateQueries({
        queryKey: key,
        refetchType: 'none'
      });
      if (pendingGate === undefined) {
        throttledRealtimeRefetch(qc, key);
        return;
      }
      startAuthoritativeRefetch(key, subject_0, ev, pendingGate.cursor);
      return;
    }
    const removesRow = isTerminalEventRef.current?.(wrapped) === true;
    if (removesRow) {
      const rowId = extractRowId(rowSubjectTemplatesRef.current, subject_0, rowKeyRef.current);
      const rowKey = rowKeyRef.current;
      if (rowId !== null && rowKey !== undefined) {
        recordTerminalRow(qc, key, rowKey, rowId);
      }
      if (rowId !== null) {
        rowBufferRef.current.delete(rowId);
        staleRowsRef.current.delete(rowId);
      }
      const viewportEntity = viewportEntityRef.current;
      if (viewportEntity !== undefined) {
        rowBufferRef.current.delete(viewportEntity);
        staleRowsRef.current.delete(viewportEntity);
      }
    }
    // ─── Per-row viewport gate ────────────────────────────────────────
    // Row-keyed list hook + a VisibilityController installed: an event
    // whose card is scrolled offscreen is BUFFERED, not applied — the
    // cache patch (and the card re-render) is deferred until the card
    // scrolls back in (`registry.onEntityVisible` → buffer replay). `add`
    // recipes accumulate, so buffered events are replayed in order, never
    // dropped; a row offscreen past `ROW_BUFFER_CAP` falls back to a
    // refetch. `isEntityVisible` fails open for entities the registry
    // never saw, so detail hooks / the mount gap are never gated.
    const rowTemplates = rowSubjectTemplatesRef.current;
    if (!removesRow && registry !== null && applyFn !== undefined && rowTemplates.length > 0) {
      const rowId_0 = extractRowId(rowTemplates, subject_0, rowKeyRef.current);
      if (rowId_0 !== null && registry.hasVisibility() && !registry.isEntityVisible(rowId_0)) {
        if (!staleRowsRef.current.has(rowId_0)) {
          const buffers = rowBufferRef.current;
          const existing = buffers.get(rowId_0);
          if (existing === undefined) {
            buffers.set(rowId_0, [wrapped]);
            if (process.env.NODE_ENV !== 'production') {
              _bumpPerf('dispatch.rowGateBuffered');
            }
          } else if (existing.length >= ROW_BUFFER_CAP) {
            // Overflow — drop the replay buffer; scroll-in will refetch.
            buffers.delete(rowId_0);
            staleRowsRef.current.add(rowId_0);
            if (process.env.NODE_ENV !== 'production') {
              _bumpPerf('dispatch.rowGateOverflows');
            }
          } else {
            existing.push(wrapped);
            if (process.env.NODE_ENV !== 'production') {
              _bumpPerf('dispatch.rowGateBuffered');
            }
          }
        }
        if (pendingGate !== undefined) admitEvent(pendingGate.key, pendingGate.cursor);
        return;
      }
    }
    // ─── Per-query viewport gate ──────────────────────────────────────
    // Arg-bound list hook (`useGetTrades`, `useGetMarketTraders`): the
    // WHOLE query belongs to ONE market. While that market's card is
    // offscreen, buffer every event keyed by the entity — reusing the
    // same `rowBufferRef` + `onEntityVisible` flush + overflow→refetch
    // path as the per-row gate. `isEntityVisible` fails open for entities
    // the registry never saw, so a detail page (where the trades ARE the
    // screen, not a feed card) is never gated.
    const vpEntity = viewportEntityRef.current;
    if (!removesRow && registry !== null && applyFn !== undefined && vpEntity !== undefined && registry.hasVisibility() && !registry.isEntityVisible(vpEntity)) {
      if (!staleRowsRef.current.has(vpEntity)) {
        const buffers_0 = rowBufferRef.current;
        const existing_0 = buffers_0.get(vpEntity);
        if (existing_0 === undefined) {
          buffers_0.set(vpEntity, [wrapped]);
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('dispatch.rowGateBuffered');
          }
        } else if (existing_0.length >= ROW_BUFFER_CAP) {
          // Overflow — drop the replay buffer; scroll-in will refetch.
          buffers_0.delete(vpEntity);
          staleRowsRef.current.add(vpEntity);
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('dispatch.rowGateOverflows');
          }
        } else {
          existing_0.push(wrapped);
          if (process.env.NODE_ENV !== 'production') {
            _bumpPerf('dispatch.rowGateBuffered');
          }
        }
      }
      if (pendingGate !== undefined) admitEvent(pendingGate.key, pendingGate.cursor);
      return;
    }
    // Devtool dispatch trace — _runDispatchBranch returns the branch tag
    // + any error message so we can emit `realtime:dispatch` with full
    // context. Extracted to a helper so the closure stays free of `let`
    // mutation (React Compiler rejects mutable bindings inside memoized
    // callbacks, which would otherwise force this whole hook out of the
    // compiler's optimisation set — file 05 §react-compiler-invariant).
    // Timing is only consumed by the devtool `realtime:dispatch` trace.
    // The explicit localhost local-SVM proof can opt in on a production
    // build; all other production pages avoid the timing reads.
    const devtoolEnabled = isDevtoolEnabled();
    const dispatchT0 = devtoolEnabled ? nowMonotonic() : 0;
    const specName = specNameRef.current;
    const beforeImmediateApply = pendingGate !== undefined && applyImmediatelyRef.current ? qc.getQueryData(key) : undefined;
    const outcome_0 = _runDispatchBranch(qc, key, wrapped, applyFn, signalFn, specName, rowKeyRef.current, reconcileOnInsertRef.current, removesRow || applyImmediatelyRef.current, snapshotSequenceRef.current);
    const immediateApplyAccepted = !admitSequenceOnlyOnMutationRef.current || qc.getQueryData(key) !== beforeImmediateApply;
    if (outcome_0.error === null && pendingGate !== undefined && immediateApplyAccepted) {
      admitEvent(pendingGate.key, pendingGate.cursor);
    }
    // Authoritative structural reconcile (MOR-923). When the event subject is
    // one of this query's concrete reconcile-always subjects, fire ONE
    // throttled REST refetch —
    // regardless of the dispatch outcome above — so a rebuy into an
    // already-cached position or a sell (neither grows the list, so
    // `reconcileOnInsert` never fires; the applyEvent leaves the row/summary
    // untouched) relearns balance / cost basis / rank the wire does not carry.
    // Throttled per queryKey (≥2s), so a bounded market event burst produces
    // one leading request plus one trailing convergence request.
    if (subjectMatchesAny(reconcileAlwaysSubjectsRef.current, subject_0)) {
      void qc.invalidateQueries({
        queryKey: key,
        refetchType: 'none'
      });
      throttledRealtimeRefetch(qc, key);
    }
    if (outcome_0.error !== null) {
      // applyEvent:error is a PRODUCTION telemetry channel (Better Stack
      // via the translator) — must fire in all environments.
      sdkEvents.emit('applyEvent:error', {
        specName,
        subject: subject_0,
        queryKey: key,
        error: outcome_0.error
      });
      throttledRealtimeRefetch(qc, key);
    }
    // realtime:dispatch is a DEVTOOL-ONLY trace consumed solely by
    // `useSdkRealtimeDispatch`. It is disabled on production pages unless
    // the local-SVM browser proof explicitly enables the devtool bus.
    if (devtoolEnabled) {
      const dispatchDuration = Math.max(0, nowMonotonic() - dispatchT0);
      sdkEvents.emit('realtime:dispatch', {
        specName,
        subject: subject_0,
        queryKey: key,
        queryHash: hashKey(key),
        branch: outcome_0.branch,
        durationMs: dispatchDuration,
        error: outcome_0.error
      });
      // Histogram observation — same duration, recorded into the
      // log-spaced bucket so we can read p50/p95/p99 across the
      // session (the dispatch ring buffer caps at 500 events).
      _observe('dispatchDurationMs', dispatchDuration);
    }
    // Async side-effect (codegen-emitted from `prepend:` recipes).
    // Fires in parallel with applyEvent so the same event can drive
    // both a sync cache patch AND an async fetch-then-prepend. Errors
    // are caught at the recipe layer — never surface here.
    const onEventFn = onEventRef.current;
    if (onEventFn !== undefined) {
      try {
        onEventFn(wrapped);
      } catch (err) {
        console.error('[morfi-sdk] onEvent handler threw:', err);
      }
    }
  },
  // `registry` is the process-wide singleton from context — stable for
  // the hook's lifetime; listed for exhaustive-deps correctness.
  [qc, registry, startAuthoritativeRefetch]);
  // Phase 2 · subscribe-before-snapshot buffering (Binance steps 1-2).
  // Until the first REST snapshot resolves, a NATS event has no cached
  // row to patch — `applyEvent(prev=undefined)` bails and the event is
  // lost. Buffer pre-snapshot events; `flushBuffer` replays them once the
  // snapshot lands. After that, events apply live. Stable identity:
  // `applyNow` is `[qc]`-stable, so `dispatchEvent` is too — the registry
  // never re-acquires.
  const dispatchEvent = useCallback((subject_1, ev_0) => {
    if (!snapshotLandedRef.current) {
      const buf = bufferRef.current;
      if (buf.length < PRE_SNAPSHOT_BUFFER_CAP) {
        buf.push({
          subject: subject_1,
          ev: ev_0
        });
      } else {
        bufferOverflowRef.current = true;
        if (process.env.NODE_ENV !== 'production') _bumpPerf('buffer.overflow');
      }
      return;
    }
    applyNow(subject_1, ev_0);
  }, [applyNow]);
  // Stable, value-equal references for the subject arrays. Codegen-emitted
  // specs allocate fresh arrays each render; we collapse to a joined-key
  // + `useMemo` so the effects below re-fire only on value change. This
  // is the established pattern in `src/provider/useNatsSubscription.ts`.
  const domainPinsKey = opts.domainPins.join('|');
  const domainPinsStable = useMemo(() => domainPinsKey.split('|').filter(s => s.length > 0), [domainPinsKey]);
  // T16001 · perEntitySubjects resolution (spec §431). The downstream
  // `.join('|')` + useMemo below produces a value-stable array reference
  // from the joined-key string — so even if the user's fn returns a new
  // array each render, `perEntityStable` (used by the acquire effects) is
  // stable for value-equal output. Codegen-emitted fns are cheap (return
  // a hardcoded subject list), so running on every render is negligible.
  const perEntitySubjectsResolved = opts.perEntitySubjectsFn === undefined ? opts.perEntitySubjects : opts.perEntitySubjectsFn(opts.perEntitySubjectsArgs);
  const perEntityKey = perEntitySubjectsResolved.join('|');
  const perEntityStable = useMemo(() => perEntityKey.split('|').filter(s_0 => s_0.length > 0), [perEntityKey]);
  const fetchOnceScopeHash = `${queryScopeHash}:subjects:${perEntityKey}`;
  // Fan-out subjects (array args) are viewport-gated per entity and never
  // part of the handoff fence: the fence would otherwise wait on subjects
  // that stay off the wire while their rows are hidden. A fan-out subject
  // whose gate resolves to `null` (wildcard id, or the call site opting it
  // out) is an ordinary always-on, fenced subject.
  const gatedKey = (opts.gatedSubjects ?? []).join('|');
  const subjectGateIdRef = useRef(opts.subjectGateId);
  subjectGateIdRef.current = opts.subjectGateId;
  const subjectSplit = useMemo(() => partitionGatedSubjects(perEntityStable, gatedKey, subject_2 => subjectGateIdRef.current?.(subject_2)), [perEntityStable, gatedKey]);
  const handoffSubjects = subjectSplit.alwaysOn;
  // A client-side realtime query's TRUSTED snapshot must post-date concrete
  // JetStream interest: an event can land after an earlier snapshot but
  // before the server begins delivering its subject. Eager hooks (the
  // default) still fetch on mount for first paint and re-fetch once interest
  // lands; only that post-wire fetch releases the pre-snapshot buffer.
  // `eagerSnapshot: false` hooks hold REST until interest lands or the
  // handoff deadline force-opens the gate. SSR has no registry and remains
  // an ordinary prefetch.
  const requiresWireHandoff = registry !== null && opts.enabled !== false && handoffSubjects.length > 0 && registry.awaitConcreteWireInterest !== undefined;
  const [wireReadyScopeHash, setWireReadyScopeHash] = useState(null);
  const [wireRetry, setWireRetry] = useState(0);
  const [snapshotReadyScopeHash, setSnapshotReadyScopeHash] = useState(null);
  const [snapshotRetry, setSnapshotRetry] = useState(0);
  const wireHandoffAttemptRef = useRef(null);
  // Scope under which the wire was REST-only (give-up). When a late wire
  // bumps the scope, the attempt (and its expired deadline) is dropped so the
  // fresh handoff gets a real deadline instead of a 0 ms force-open.
  const restOnlyScopeRef = useRef(null);
  const wireHandoffRegistryRef = useRef(null);
  // Handoff scope whose snapshot is being re-fenced (scheduled or in
  // flight). State, not a ref: the snapshot effect must read it from the
  // same render as `snapshotReady`, or a passive effect flushed between a
  // ref flip and the matching commit could fence the doomed request.
  const [refenceScope, setRefenceScope] = useState(null);
  const wireReadyScopeHashRef = useRef(null);
  useEffect(() => {
    wireReadyScopeHashRef.current = wireReadyScopeHash;
  }, [wireReadyScopeHash]);
  const wireReady = !requiresWireHandoff || wireReadyScopeHash === handoffScopeHash;
  const snapshotReady = !requiresWireHandoff || snapshotReadyScopeHash === handoffScopeHash;
  // A replaced consumer starts on DeliverNew: events between the old and the
  // new one are gone. Always-on subjects re-fence through the post-wire fetch
  // above; an all-gated query has no such fetch and would resume live over a
  // hole, so it takes one throttled snapshot per wire replacement instead.
  const refetchesOnWireReplace = !requiresWireHandoff && registry !== null && opts.enabled !== false && subjectSplit.gated.length > 0;
  const refetchesOnWireReplaceRef = useRef(refetchesOnWireReplace);
  useEffect(() => {
    refetchesOnWireReplaceRef.current = refetchesOnWireReplace;
  }, [refetchesOnWireReplace]);
  const seenHandoffScopeRef = useRef(null);
  useEffect(() => {
    const previous_0 = seenHandoffScopeRef.current;
    seenHandoffScopeRef.current = handoffScopeHash;
    if (previous_0 === null || previous_0 === handoffScopeHash) return;
    // Only a wire bump under the same query key: a key change is a new query.
    if (!previous_0.startsWith(`${queryScopeHash}:wire:`)) return;
    if (!refetchesOnWireReplaceRef.current) return;
    throttledRealtimeRefetch(qc, queryKeyRef.current);
  }, [qc, handoffScopeHash, queryScopeHash]);
  // Eager snapshots (the default, fetch-once owners included) render while
  // the wire installs; the handoff effect below re-fences them with one
  // fresh post-wire fetch once interest is real, so buffered events only go
  // live against post-wire data. A fetch-once owner spends its one-fetch
  // contract on that re-fetch (its completed pre-wire snapshot is reset
  // first). `eagerSnapshot: false` keeps the strict gate.
  const eagerSnapshot = opts.eagerSnapshot !== false;
  const initialSnapshotReady = eagerSnapshot || wireReady;
  useEffect(() => {
    const release = opts.fetchOnce === true ? retainFetchOnceRealtimeOwner(qc, queryKeyRef.current, fetchOnceScopeHash) : undefined;
    return () => release?.();
  }, [fetchOnceScopeHash, opts.fetchOnce, qc]);
  // Keep `gatePatternsRef` current so the empty-deps `dispatchEvent`
  // callback can recover the matched subject pattern for the sequence
  // gate. Per-entity patterns first (more specific), then domain pins.
  useEffect(() => {
    gatePatternsRef.current = [...perEntityStable, ...domainPinsStable];
  }, [perEntityStable, domainPinsStable]);
  // ─── (1) Domain-pins acquire ──────────────────────────────────────────
  // Always-on while ANY hook in this domain is mounted. The registry
  // surface doesn't take a callback for domain pins (interest is shared
  // across the trie); we still record the queryKey so debug attribution
  // works.
  useEffect(() => {
    if (opts.enabled === false) return;
    if (registry === null) return;
    const handle = registry.acquireDomain(opts.domain, domainPinsStable, [...queryKeyRef.current]);
    return () => {
      handle.release();
    };
  }, [registry, opts.domain, opts.enabled, domainPinsStable]);
  // ─── Reacquire callback — fires after a long observer gap or a missed
  //     warm-subscription event. Default impl refetches the hook's query
  //     (cheap; reuses spec's queryFn). Custom impls are forwarded from
  //     the spec via `createHook`. Stable identity keeps registry wiring
  //     unchanged across re-renders.
  //
  // Compiler-clean: latest opts.* tracked via post-commit `useEffect`,
  // read through refs inside the empty-deps callback. Registry-fired
  // reacquire events always happen AFTER the post-commit effect, so
  // refs are guaranteed up-to-date by the time we read them.
  const onReacquireRef = useRef(opts.onReacquire);
  const onReacquireArgsRef = useRef(opts.onReacquireArgs);
  useEffect(() => {
    onReacquireRef.current = opts.onReacquire;
    onReacquireArgsRef.current = opts.onReacquireArgs;
  });
  const stableReacquire = useCallback(info => {
    const recipe = onReacquireRef.current;
    if (recipe === 'none') return;
    if (typeof recipe === 'function') {
      try {
        void recipe(onReacquireArgsRef.current, {
          queryClient: qc,
          lastSeq: info.lastSeq,
          gapMs: info.gapMs
        });
      } catch (err_0) {
        console.error('[morfi-sdk] custom onReacquire threw:', err_0);
      }
      return;
    }
    // Default: refetch this hook's query. ONE HTTP call — restores
    // cache freshness after the gap with zero consumer-side wiring.
    //
    // CRITICAL: route through `refetchRealtimeQuery`, NOT
    // `qc.refetchQueries({queryKey})`. The latter uses
    // `Query.options.queryFn` (last-setOptions-wins — typically the
    // subscriber sentinel), which resolves with cached data and fires
    // no HTTP request. The helper iterates observers and refetches
    // through the parent (data-owning) observer's options, so the
    // real fetch goes out. See `refetchRealtimeQuery.ts` for the full
    // rationale.
    //
    // DEFER VIA MICROTASK — load-bearing for the mount-time reacquire.
    // The registry fires `onReacquire` SYNCHRONOUSLY inside
    // `registerObserver`, which runs from the per-entity acquire
    // `useEffect` below. That effect is a passive effect declared
    // BEFORE the `useQuery` call further down — and `useQuery`'s
    // observer only subscribes to the QueryCache in ITS OWN passive
    // effect, registered after ours. So at the instant this callback
    // runs on a fresh mount (e.g. a re-keyed carousel slide remounting
    // a `useGetMarket`), the data-owning `QueryObserver` is NOT yet in
    // `query.observers`. `refetchRealtimeQuery` would iterate zero
    // data-owning observers and silently no-op — no HTTP, no catch-up,
    // permanent staleness for any surface that unmounts/remounts
    // (carousel hero, tab switch, virtualized row recycle).
    //
    // React flushes the whole passive-effect batch of a commit
    // synchronously within one task; a `queueMicrotask` therefore runs
    // AFTER `useQuery`'s subscribe effect has attached the parent
    // observer, so the refetch finds it and the real fetch goes out.
    // Re-read the queryKey from the ref at flush time so an args change
    // between scheduling and flush still targets the current entry.
    queueMicrotask(() => {
      void refetchRealtimeQuery(qc, [...queryKeyRef.current]);
    });
  }, [qc]);
  // ─── (2) Per-entity ALWAYS-ON path (non-viewport-gated) ───────────────
  useEffect(() => {
    // The query key lives in a ref; the hash is the explicit lifecycle
    // dependency that tears down and rebinds the registry observer on re-key.
    void queryScopeHash;
    if (opts.enabled === false) return;
    if (registry === null) return;
    if (opts.responseSubjects !== undefined) return;
    if (perEntityStable.length === 0) return;
    if (opts.viewportGated) return;
    const {
      alwaysOn,
      gated
    } = subjectSplit;
    // A registry without the per-entity gate (bare/test) holds fan-out
    // subjects always-on, exactly as before gating existed.
    if (gated.length === 0 || registry.acquireEntityGated === undefined) {
      const handle_0 = registry.acquireEntity(perEntityStable, dispatchEvent, [...queryKeyRef.current], stableReacquire);
      return () => {
        handle_0.release();
      };
    }
    const handles = [registry.acquireEntityGated(gated, dispatchEvent, [...queryKeyRef.current], stableReacquire, subject_3 => subjectGateIdRef.current?.(subject_3))];
    if (alwaysOn.length > 0) {
      handles.push(registry.acquireEntity(alwaysOn, dispatchEvent, [...queryKeyRef.current], stableReacquire));
    }
    return () => {
      for (const handle_1 of handles) handle_1.release();
    };
  }, [registry, perEntityStable, opts.responseSubjects, subjectSplit, opts.enabled, opts.viewportGated, queryScopeHash, dispatchEvent, stableReacquire]);
  // Concrete interest is only real once FilterManager's async consumer
  // update has succeeded. Gate the buffer-releasing snapshot on that promise;
  // a reconnect tears it down and retries rather than letting a stale
  // hydrated cache become the new handoff boundary. The gate is
  // best-effort, not a hard prerequisite: one deadline per handoff
  // ATTEMPT (query scope × subject set × registry instance — NOT per wire
  // version, so provider reconnect churn cannot restart the clock)
  // force-opens it so REST still loads when NATS is degraded. When real
  // readiness arrives after a forced open — or after an eager snapshot that
  // started before interest was concrete — the re-fence below re-arms the
  // buffers and forces one fresh post-wire snapshot. Eager hooks arm the
  // deadline too: past it their pre-wire snapshot is fenced as-is (buffer
  // released, `deadline_forced_open` emitted) instead of waiting on a wire
  // that may never come.
  useEffect(() => {
    if (!requiresWireHandoff || registry === null) {
      // A disabled window or missing registry ends the current attempt;
      // re-enabling starts a fresh deadline instead of inheriting an
      // expired one and force-opening a healthy wire with zero grace.
      wireHandoffAttemptRef.current = null;
      return undefined;
    }
    if (registry.awaitConcreteWireInterest === undefined) return undefined;
    let cancelled = false;
    let retryTimer = null;
    let deadlineTimer = null;
    let refenceTimer = null;
    const scheduleReadinessRetry = error => {
      // The ladder keeps seeking a real wire behind an already-open gate;
      // emit only while the gate is still closed so a long degraded
      // session does not WARN per hook per rung indefinitely.
      if (wireReadyScopeHashRef.current !== handoffScopeHash) {
        emitHandoffRecovery(specNameRef.current, handoffReasonFromError(error));
      }
      const retryIndex = Math.min(wireRetry, HANDOFF_RECOVERY_RETRY_MS.length - 1);
      retryTimer = setTimeout(() => {
        setWireRetry(attempt => attempt + 1);
      }, HANDOFF_RECOVERY_RETRY_MS[retryIndex]);
    };
    let readiness;
    try {
      readiness = registry.awaitConcreteWireInterest(handoffSubjects);
    } catch (error_0) {
      scheduleReadinessRetry(error_0);
      return () => {
        cancelled = true;
        if (retryTimer !== null) clearTimeout(retryTimer);
      };
    }
    if (readiness === null) {
      restOnlyScopeRef.current = handoffScopeHash;
      setWireReadyScopeHash(handoffScopeHash);
      return undefined;
    }
    if (wireHandoffRegistryRef.current !== registry) {
      wireHandoffRegistryRef.current = registry;
      wireHandoffAttemptRef.current = null;
    }
    const attemptKey = `${queryScopeHash}|${perEntityKey}`;
    let attempt_0 = wireHandoffAttemptRef.current;
    if (attempt_0 === null || attempt_0.key !== attemptKey) {
      attempt_0 = {
        key: attemptKey,
        deadlineAt: nowMonotonic() + WIRE_HANDOFF_DEADLINE_MS + Math.random() * WIRE_HANDOFF_JITTER_MS,
        forcedScope: null
      };
      wireHandoffAttemptRef.current = attempt_0;
    }
    const armDeadline = wireReadyScopeHashRef.current !== handoffScopeHash;
    if (armDeadline) {
      deadlineTimer = setTimeout(() => {
        if (cancelled) return;
        const live = wireHandoffAttemptRef.current;
        if (live === null || live.key !== attemptKey) return;
        if (wireReadyScopeHashRef.current === handoffScopeHash) return;
        const firstForce = live.forcedScope !== handoffScopeHash;
        live.forcedScope = handoffScopeHash;
        // State before emit: a throwing bus listener must not be able
        // to latch the gate shut between marking and opening. An eager
        // snapshot already in the cache is fenced as-is — a REST-only
        // re-fetch now would be just as pre-wire; the strict path (no data
        // yet) lets the snapshot effect issue its first fetch.
        setWireReadyScopeHash(handoffScopeHash);
        if (qc.getQueryData(queryKeyRef.current) !== undefined) {
          setSnapshotReadyScopeHash(handoffScopeHash);
        }
        if (firstForce) emitHandoffRecovery(specNameRef.current, 'deadline_forced_open');
      }, Math.max(0, attempt_0.deadlineAt - nowMonotonic()));
    }
    const refenceForcedOpenSnapshot = async () => {
      // The forced-open or eager snapshot predates delivery. Take the boundary down
      // and re-arm the pre-snapshot buffer so events arriving during the
      // fresh fetch are sequence-gated against it instead of racing it,
      // then require a NEW post-wire fetch. Managed polling waits for any
      // pre-wire request to finish first; legacy handoffs cancel it.
      // The boundary is restored here (not by the ordinary
      // snapshot effect, which stands down while this runs) once the fresh
      // fetch settles.
      snapshotLandedRef.current = false;
      bufferRef.current = [];
      bufferOverflowRef.current = false;
      rowBufferRef.current.clear();
      staleRowsRef.current.clear();
      setLiveSinceMountFenceSeq(null);
      if (opts.fetchOnce === true) {
        resetFetchOnceRealtimeSnapshot(qc, queryKeyRef.current, fetchOnceScopeHash);
      }
      setSnapshotReadyScopeHash(null);
      try {
        // TanStack only honours `cancelRefetch` when the query already has
        // data; a first fetch in flight on an empty cache would silently
        // dedupe. Cancel explicitly so the refetch below is always a fresh
        // post-wire request, and refetch through the data-owning observer:
        // `qc.refetchQueries` runs whichever queryFn last called setOptions,
        // typically a subscriber sentinel that resolves from cache with no
        // HTTP at all.
        // A sibling instance on this key may already have started, or even
        // landed, the post-wire fetch for this scope; dedupe onto it instead
        // of aborting it and paying the request twice.
        if (opts.pollingManaged === true && qc.getQueryState(queryKeyRef.current)?.fetchStatus === 'fetching' && lastSnapshotFetchWasPostWire(qc, queryKeyRef.current, handoffScopeHash) !== true) {
          // Let an existing fallback request finish, then take a fresh post-wire
          // snapshot. Aborting it cannot stop server work and creates overlap.
          await refetchRealtimeQuery(qc, queryKeyRef.current);
          if (cancelled) return;
        }
        const postWire = lastSnapshotFetchWasPostWire(qc, queryKeyRef.current, handoffScopeHash) === true;
        const state = qc.getQueryState(queryKeyRef.current);
        const postWireSettled = postWire && state?.status === 'success' && state.fetchStatus === 'idle';
        if (!postWireSettled) {
          if (opts.pollingManaged !== true && !(postWire && state?.fetchStatus === 'fetching')) {
            await qc.cancelQueries({
              queryKey: queryKeyRef.current,
              exact: true
            });
          }
          await refetchRealtimeQuery(qc, queryKeyRef.current);
        }
        if (opts.fetchOnce === true) {
          markFetchOnceRealtimeSnapshotComplete(qc, queryKeyRef.current, fetchOnceScopeHash);
        }
      } finally {
        if (!cancelled) setSnapshotReadyScopeHash(handoffScopeHash);
        setRefenceScope(current => current === handoffScopeHash ? null : current);
      }
    };
    void readiness.then(() => {
      if (cancelled) return;
      if (deadlineTimer !== null) clearTimeout(deadlineTimer);
      const live_0 = wireHandoffAttemptRef.current;
      const wasForced = live_0 !== null && live_0.forcedScope === handoffScopeHash;
      if (live_0 !== null) live_0.forcedScope = null;
      // An eager snapshot whose fetch started before this interest was
      // concrete predates delivery exactly like a forced-open one, and a
      // plain refetch would dedupe onto it while it is still in flight.
      const preWireSnapshot = lastSnapshotFetchWasPostWire(qc, queryKeyRef.current, handoffScopeHash) === false;
      // A waiter resolved by the provider's REST-only give-up is not a
      // wire: nothing will ever be delivered, so there is no gap to
      // close and no re-fence to run.
      const needsRefence = (wasForced || preWireSnapshot) && registry.isWireInterestDisabled?.() !== true;
      // Mark in the same batch as opening the gate so the ordinary snapshot
      // effect stands down instead of fencing the pre-wire fetch first.
      if (needsRefence) setRefenceScope(handoffScopeHash);
      setWireReadyScopeHash(handoffScopeHash);
      if (!needsRefence) return;
      refenceTimer = setTimeout(() => {
        if (cancelled) return;
        void refenceForcedOpenSnapshot();
      }, Math.floor(Math.random() * WIRE_HANDOFF_JITTER_MS));
    }).catch(error_1 => {
      if (cancelled) return;
      scheduleReadinessRetry(error_1);
    });
    return () => {
      cancelled = true;
      if (deadlineTimer !== null) clearTimeout(deadlineTimer);
      if (retryTimer !== null) clearTimeout(retryTimer);
      if (refenceTimer !== null) {
        clearTimeout(refenceTimer);
        // Scheduled but never run: release the stand-down for this scope.
        setRefenceScope(current => current === handoffScopeHash ? null : current);
      }
    };
  }, [handoffScopeHash, registry, handoffSubjects, requiresWireHandoff, wireRetry, opts.pollingManaged, queryScopeHash, perEntityKey, fetchOnceScopeHash, opts.fetchOnce, qc]);
  // ─── (3) Viewport-gated callback ref ──────────────────────────────────
  // FR1 patch — viewport gating leak fix.
  //
  // The registry's `acquireWhenVisible(subjects, onEvent, queryKey)`
  // returns a long-lived `VisibilityRefCallback` whose closure owns the
  // IntersectionObserver binding + per-entity acquisition state
  // (`currentElement`, `unbind`, `acq`). That refCb MUST be the same
  // instance across renders — calling `acquireWhenVisible` again creates
  // a fresh closure and orphans the previous IntersectionObserver
  // binding (the observer keeps firing into a dead callback the next
  // time the element scrolls).
  //
  // Previous shape called `acquireWhenVisible` inside the ref callback
  // body — every React-driven rebind leaked. Fix: hold the registry-
  // returned refCb in a ref, manage its lifecycle in a useEffect keyed
  // on the deps that should rebind (subjects / dispatcher / mode), and
  // give `setVisibilityRef` empty-deps stable identity so React doesn't
  // churn-rebind across renders.
  //
  // `boundRef.current` is set to `true` unconditionally on every non-null
  // element attach — the never-bound guard reads it inside its effect-
  // gated check below.
  const refCbRef = useRef(null);
  const currentElementRef = useRef(null);
  useEffect(() => {
    // Keep the callback binding scoped to the current query even when its
    // concrete subjects are unchanged across an A → B navigation.
    void queryScopeHash;
    // Tear down any prior binding before swapping the refCb.
    if (refCbRef.current !== null && currentElementRef.current !== null) {
      refCbRef.current(null);
    }
    refCbRef.current = null;
    if (opts.enabled === false || registry === null || !opts.viewportGated || perEntityStable.length === 0) {
      return undefined;
    }
    const refCb = registry.acquireWhenVisible(perEntityStable, dispatchEvent, [...queryKeyRef.current], stableReacquire);
    refCbRef.current = refCb;
    // Re-attach to the currently-mounted element. Without this, deps
    // change → fresh refCb but no element bind until the next React-
    // driven setVisibilityRef call (which only fires on element identity
    // change, not on subject-set change).
    if (currentElementRef.current !== null) {
      refCb(currentElementRef.current);
    }
    return () => {
      refCb(null);
      if (refCbRef.current === refCb) refCbRef.current = null;
    };
  }, [registry, perEntityStable, opts.enabled, opts.viewportGated, queryScopeHash, dispatchEvent, stableReacquire]);
  // Stable identity — empty deps — so React never re-attaches the ref
  // on re-render. Element-bind/unbind flows through `refCbRef.current`.
  const setVisibilityRef = useCallback(el => {
    boundRef.current = el !== null;
    currentElementRef.current = el;
    refCbRef.current?.(el);
  }, []);
  // ─── (4) Never-bound dev guard (T2003) ────────────────────────────────
  // Fires once at 5 s in dev / 30 s in prod. Gate INSIDE the effect, not
  // around the `useEffect` call (Rules of Hooks compliance — the effect
  // is unconditionally registered regardless of `viewportGated`).
  useEffect(() => {
    if (opts.enabled === false) return;
    if (!opts.viewportGated) return;
    const isDev = process.env.NODE_ENV !== 'production';
    const delay = isDev ? NEVER_BOUND_DEV_MS : NEVER_BOUND_PROD_MS;
    const timer = setTimeout(() => {
      if (boundRef.current) return;
      if (isDev) {
        console.warn(`[morfi-sdk] viewportGated hook never bound an element after ${delay / 1000}s — did you forget to spread { ref } on a DOM element?`, {
          specName: opts.specName,
          queryKey: opts.queryKey
        });
        neverBoundDevtoolEmit({
          specName: opts.specName,
          queryKey: [...opts.queryKey]
        });
      } else {
        // Prod: rate-limit to one event per spec per session via
        // `sessionStorage`. The storage may throw under Safari
        // private-browsing — swallow and skip the throttle.
        const storageKey = `morfi-vp-unbound:${opts.specName}`;
        try {
          if (typeof sessionStorage !== 'undefined') {
            if (sessionStorage.getItem(storageKey) !== null) return;
            sessionStorage.setItem(storageKey, '1');
          }
        } catch {
          // Ignore — proceed without throttling.
        }
        neverBoundProdTelemetry({
          specName: opts.specName
        });
      }
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [opts.enabled, opts.viewportGated, opts.specName, opts.queryKey]);
  // FR2 fix #2 — wire the canonical realtime triple-override (staleTime /
  // retryOnMount / refetchOnMount) so reconnect-driven storms and the
  // hydration-vs-NATS race are blocked per spec L1296. The connectivity
  // handle is read from the module-singleton store so the function-form
  // refetchOnMount can compare `query.state.dataUpdatedAt` against
  // `lastReconnectAt`.
  // Compose the canonical realtime triple-override with an optional
  // `select` from the caller. List hooks pass `select: () => null` so the
  // page-level observer only ever notifies on isPending / isError / status
  // changes — data churn is invisible to the consumer page (per TanStack
  // docs §Render Optimizations · select).
  // Subscribe to the FSM `state` field. The hook re-renders on every
  // connectivity transition; useQuery's observer then sees fresh options
  // and calls `setOptions`, which re-evaluates the function-form
  // `refetchInterval` — exactly the TanStack-blessed render-optimization
  // path for adaptive intervals.
  // https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
  //   "If set to a function, the function will be executed with the latest
  //    data and query to compute a frequency."
  React.useSyncExternalStore(subscribeConnectivityState, getConnectivityStateSnapshot, getConnectivityStateSnapshot);
  React.useEffect(() => {
    if (opts.pollingHasNats !== true || opts.fetchOnce === true || opts.enabled === false) return;
    const checkCoverage = () => {
      if (registry?.isWireInterestDisabled?.() === true || !hasPollingCoverage(registry, handoffSubjects, subjectSplit.gated, subjectGateIdRef.current) || wireHandoffAttemptRef.current?.forcedScope === handoffScopeHash) {
        requirePollingRecovery(qc, opts.queryKey, handoffScopeHash);
      }
    };
    checkCoverage();
    return subjectSplit.gated.length > 0 ? registry?.onEntityVisibilityChange?.(checkCoverage) : undefined;
  }, [qc, opts.queryKey, opts.pollingHasNats, opts.fetchOnce, opts.enabled, registry, handoffSubjects, subjectSplit.gated, handoffScopeHash]);
  const fencedQueryFn = useCallback(async context => filterTerminalRows(qc, context.queryKey, await opts.queryFn(context)), [qc, opts.queryFn]);
  const dataQueryFn = useCallback(context_0 => {
    // Remember whether this snapshot post-dates concrete interest; the
    // handoff decides between trusting it and forcing a post-wire re-fetch.
    // A fetch issued behind a deadline-forced gate never does, and a
    // registry that cannot answer counts as pre-wire: the failure mode is
    // one extra fetch, never a fence on data that predates delivery.
    const forcedOpen = wireHandoffAttemptRef.current?.forcedScope === handoffScopeHash;
    recordSnapshotFetchStart(qc, opts.queryKey, handoffScopeHash, !requiresWireHandoff || registry === null || registry.isWireInterestDisabled?.() === true || !forcedOpen && registry.hasConcreteWireInterest?.(handoffSubjects) === true);
    return opts.fetchOnce === true ? runFetchOnceRealtimeSnapshot(qc, opts.queryKey, fetchOnceScopeHash, () => Promise.resolve(fencedQueryFn(context_0))) : opts.pollingManaged !== true ? fencedQueryFn(context_0) : observePollingFetch(qc, opts.queryKey, () => Promise.resolve(fencedQueryFn(context_0)), opts.pollingHasNats !== true || hasPollingCoverage(registry, handoffSubjects, subjectSplit.gated, subjectGateIdRef.current) && !forcedOpen, context_0.signal);
  }, [fetchOnceScopeHash, fencedQueryFn, opts.pollingManaged, opts.pollingHasNats, handoffScopeHash, opts.fetchOnce, opts.queryKey, handoffSubjects, subjectSplit.gated, qc, registry, requiresWireHandoff]);
  const baseQueryOptions = realtimeQueryOptions({
    queryKey: opts.queryKey,
    queryFn: dataQueryFn,
    ...(opts.meta !== undefined ? {
      meta: opts.meta
    } : {})
  }, connectivityStore.getState(), opts.refetchInterval, opts.fetchOnce === true);
  const finalOptions = {
    ...baseQueryOptions,
    ...(opts.fetchOnce !== true && opts.authoritativeRefetchOnMount === true ? {
      refetchOnMount: skipAutomaticAuthoritativeRefetchOnMount,
      retryOnMount: false
    } : opts.retryFailedSnapshotOnMount === true ? {
      retryOnMount: true
    } : {}),
    ...(opts.fetchOnce !== true && opts.authoritativeRefetchOnWindowFocus === true ? {
      refetchOnWindowFocus: 'always'
    } : {}),
    ...(opts.enabled !== undefined || requiresWireHandoff || fetchOnceOwnerActive ? {
      enabled: opts.enabled !== false && initialSnapshotReady && (opts.fetchOnce === true || !fetchOnceOwnerActive)
    } : {}),
    ...(opts.fetchOnce === true ? {
      refetchInterval: false
    } : opts.refetchInterval !== undefined ? {
      refetchInterval: opts.refetchInterval
    } : {}),
    ...(opts.retry === false ? {
      retry: false
    } : {}),
    ...(opts.select !== undefined ? {
      select: opts.select
    } : {})
  };
  const query = useQuery(finalOptions);
  const responseScopeData = opts.responseSubjects === undefined ? undefined : query.data;
  const responseSubjectKey = useMemo(() => opts.responseSubjects === undefined ? '' : [...new Set([...perEntityStable, ...responseSubjects(responseScopeData, opts.responseSubjects)])].join('|'), [perEntityStable, responseScopeData, opts.responseSubjects]);
  useEffect(() => {
    if (registry === null || opts.enabled === false || opts.responseSubjects === undefined || responseSubjectKey === '') return;
    void queryScopeHash;
    const subjects = responseSubjectKey.split('|');
    const queryKey_0 = [...queryKeyRef.current];
    const handle_2 = registry.acquireEntity(subjects, dispatchEvent, queryKey_0, stableReacquire);
    let cancelled_0 = false;
    // The scope was learned from REST before its wire interest existed. Fetch
    // again after admission so a lifecycle change in that gap cannot be lost.
    void Promise.resolve().then(async () => {
      const ready = registry.awaitConcreteWireInterest?.(subjects);
      if (ready == null) return;
      await ready;
      if (!cancelled_0) await refetchRealtimeQuery(qc, queryKey_0);
    }).catch(error_2 => {
      if (!cancelled_0) console.error('[morfi-sdk] response subject handoff failed:', error_2);
    });
    return () => {
      cancelled_0 = true;
      handle_2.release();
    };
  }, [registry, opts.enabled, opts.responseSubjects, responseSubjectKey, queryScopeHash, dispatchEvent, stableReacquire, qc]);
  const authoritativeMountKey = hashKey(opts.queryKey);
  const authoritativeMountRef = useRef(null);
  useEffect(() => {
    if (opts.fetchOnce === true || opts.authoritativeRefetchOnMount !== true || opts.enabled === false || !wireReady || authoritativeMountRef.current === authoritativeMountKey) {
      return;
    }
    authoritativeMountRef.current = authoritativeMountKey;
    // Find the data-owning observer explicitly. A later cache-only subscriber
    // can latch its sentinel onto the shared Query before this effect runs;
    // refetching through that sentinel would replay a persisted id envelope
    // without recovering its missing canonical entities.
    void refetchRealtimeQuery(qc, opts.queryKey, dataQueryFn);
  }, [authoritativeMountKey, opts.fetchOnce, opts.authoritativeRefetchOnMount, opts.enabled, wireReady, opts.queryKey, qc, dataQueryFn]);
  // A hydrated query is already `success`, but it was captured before this
  // client acquired its concrete NATS filter. Force one authoritative fetch
  // after readiness; only that response may release the pre-snapshot buffer.
  useEffect(() => {
    let cancelled_1 = false;
    let retryTimer_0 = null;
    // A deadline re-fence owns its whole fetch cycle (cancel-and-restart,
    // then restore the boundary itself); running the ordinary authoritative
    // fetch concurrently would either dedupe onto the doomed pre-wire
    // request or double-fetch, depending on timing.
    if (refenceScope === handoffScopeHash) return undefined;
    if (requiresWireHandoff && wireReady && !snapshotReady) {
      if (opts.fetchOnce !== true && fetchOnceOwnerActive) {
        setSnapshotReadyScopeHash(handoffScopeHash);
      } else if (registry?.isWireInterestDisabled?.() === true) {
        // REST-only provider: nothing will be delivered, so the snapshot
        // already in the cache is as authoritative as one gets.
        setSnapshotReadyScopeHash(handoffScopeHash);
      } else if (opts.fetchOnce !== true && lastSnapshotFetchWasPostWire(qc, opts.queryKey, handoffScopeHash) === true && qc.getQueryState(opts.queryKey)?.status === 'success') {
        // The settled snapshot already post-dates delivery on this wire;
        // re-fetching it would only duplicate the request.
        setSnapshotReadyScopeHash(handoffScopeHash);
      } else if (opts.fetchOnce === true && opts.reuseExistingFetchOnceSnapshot === true && qc.getQueryData(opts.queryKey) !== undefined) {
        markFetchOnceRealtimeSnapshotComplete(qc, opts.queryKey, fetchOnceScopeHash);
        setSnapshotReadyScopeHash(handoffScopeHash);
      } else if (opts.fetchOnce === true && hasCompletedFetchOnceRealtimeSnapshot(qc, opts.queryKey, fetchOnceScopeHash)) {
        setSnapshotReadyScopeHash(handoffScopeHash);
      } else {
        void refetchRealtimeQuery(qc, opts.queryKey, dataQueryFn).then(outcome_1 => {
          if (opts.fetchOnce === true && outcome_1 === 'success') {
            markFetchOnceRealtimeSnapshotComplete(qc, opts.queryKey, fetchOnceScopeHash);
          }
          if (cancelled_1) return;
          if (outcome_1 === 'success' || opts.fetchOnce === true && outcome_1 === 'error') {
            setSnapshotReadyScopeHash(handoffScopeHash);
            return;
          }
          emitHandoffRecovery(specNameRef.current, 'replay_unavailable');
          if (opts.fetchOnce === true) return;
          const retryIndex_0 = Math.min(snapshotRetry, HANDOFF_RECOVERY_RETRY_MS.length - 1);
          retryTimer_0 = setTimeout(() => {
            setSnapshotRetry(attempt_1 => attempt_1 + 1);
          }, HANDOFF_RECOVERY_RETRY_MS[retryIndex_0]);
        });
      }
    }
    return () => {
      cancelled_1 = true;
      if (retryTimer_0 !== null) clearTimeout(retryTimer_0);
    };
  }, [dataQueryFn, fetchOnceOwnerActive, fetchOnceScopeHash, opts.fetchOnce, opts.reuseExistingFetchOnceSnapshot, opts.queryKey, qc, refenceScope, registry, handoffScopeHash, requiresWireHandoff, snapshotReady, snapshotRetry, wireReady]);
  // ─── Per-row viewport gate · buffer flush ────────────────────────────
  // When a gated-offscreen row scrolls back in (registry hidden→visible
  // edge), replay its buffered events into ONE `setQueryData` — or refetch
  // if it overflowed the cap. The fold is order-preserving and correct for
  // both `set` (last-wins) and `add` (accumulate) recipes. Inert with no
  // registry (SSR / RN-without-provider).
  useEffect(() => {
    if (registry === null) return undefined;
    return registry.onEntityVisible(rowId_1 => {
      const key_0 = queryKeyRef.current;
      if (staleRowsRef.current.has(rowId_1)) {
        staleRowsRef.current.delete(rowId_1);
        rowBufferRef.current.delete(rowId_1);
        if (process.env.NODE_ENV !== 'production') {
          _bumpPerf('dispatch.rowGateOverflowRefetches');
        }
        throttledRealtimeRefetch(qc, key_0);
        return;
      }
      const buffered = rowBufferRef.current.get(rowId_1);
      if (buffered === undefined || buffered.length === 0) return;
      rowBufferRef.current.delete(rowId_1);
      const applyFn_0 = applyEventRef.current;
      if (applyFn_0 === undefined) return;
      const queryHash_1 = hashKey(key_0);
      if (process.env.NODE_ENV !== 'production') {
        markNextWrite(qc, queryHash_1, 'nats');
      }
      const rowKeyOpt = rowKeyRef.current;
      const prevBeforeFlush = rowKeyOpt !== undefined ? qc.getQueryData(key_0) : undefined;
      qc.setQueryData(key_0, prev => {
        let acc = prev;
        for (const ev_1 of buffered) {
          try {
            acc = applyFn_0(acc, ev_1);
          } catch (err_1) {
            sdkEvents.emit('applyEvent:error', {
              specName: specNameRef.current,
              subject: ev_1.subject ?? '<buffered>',
              queryKey: key_0,
              error: err_1 instanceof Error ? err_1.message : String(err_1)
            });
          }
        }
        return acc;
      });
      if (rowKeyOpt !== undefined) {
        tagNewRowsFromDiff(qc, queryHash_1, rowKeyOpt, prevBeforeFlush, qc.getQueryData(key_0));
      }
      if (process.env.NODE_ENV !== 'production') {
        _bumpPerf('dispatch.rowGateFlushed', buffered.length);
      }
    });
  }, [registry, qc]);
  // ─── Per-row viewport gate · refetch coherence ───────────────────────
  // Drop ALL row buffers when an authoritative SERVER fetch lands: a fetch
  // already reflects cumulative volume/prize, so events buffered before it
  // would double-apply on a later scroll-in flush. This MUST fire only on a
  // real fetch — a `setQueryData` realtime patch dispatches `manual: true`
  // and must NOT clear buffers (those patches are exactly what the gate
  // buffers for offscreen rows). Subscribing to the query cache — rather
  // than reading `query.dataUpdatedAt` in render — also keeps `dataUpdatedAt`
  // OUT of this hook's tracked-properties set: a render-phase read would
  // re-render every `useRealtimeQuery` on every cache patch.
  const refetchCoherenceKeyHash = hashKey(opts.queryKey);
  useEffect(() => {
    return qc.getQueryCache().subscribe(event_0 => {
      if (event_0.type === 'updated' && event_0.action.type === 'success' && event_0.action.manual !== true && hashKey(event_0.query.queryKey) === refetchCoherenceKeyHash) {
        rowBufferRef.current.clear();
        staleRowsRef.current.clear();
      }
    });
  }, [qc, refetchCoherenceKeyHash]);
  // Phase 2 · flush the pre-snapshot buffer once the first snapshot
  // settles. On success WITH a `lastSeq` watermark: replay buffered
  // events through `applyNow` — the sequence gate drops buffered events
  // already reflected in the snapshot and applies the rest in arrival
  // order (Binance steps 5-7). Without a watermark we cannot tell which
  // buffered events the snapshot already contains, so they are dropped
  // (== pre-Phase-2 behaviour) rather than risk a double-apply. On error:
  // discard the buffer and go live.
  const querySettled = (query.isSuccess && !query.isPlaceholderData || query.isError) && snapshotReady;
  useEffect(() => {
    // The reset effect also runs on wire replacement. All-gated queries stay
    // settled across that edge, so re-arm their live fence in the new scope.
    void handoffScopeHash;
    if (snapshotLandedRef.current || !querySettled) return;
    // Capture this hook instance's live-since-mount fence BEFORE flipping
    // `snapshotLandedRef` to true — otherwise a live NATS event arriving
    // via `dispatchEvent` between the flag flip and the fence read would
    // route through `applyNow` (the flag is now true), call `tagLiveRow`,
    // and bump the per-queryHash seq PAST the value we then capture.
    // That legitimately-fresh event would then be excluded from this
    // consumer's projection (tag-seq ≤ fence). Reading first ensures any
    // event arriving in this micro-window is BUFFERED, then flushed below
    // — its tag-seq strictly exceeds the captured fence. Order matters.
    setLiveSinceMountFenceSeq(readLiveSeq(qc, hashKey(queryKeyRef.current)));
    snapshotLandedRef.current = true;
    const buffered_0 = bufferRef.current;
    bufferRef.current = [];
    if (bufferOverflowRef.current) {
      bufferOverflowRef.current = false;
      emitHandoffRecovery(specNameRef.current, 'buffer_overflow');
      throttledRealtimeRefetch(qc, queryKeyRef.current);
      return;
    }
    if (buffered_0.length === 0) return;
    if (query.isSuccess) {
      const key_1 = queryKeyRef.current;
      const cached_1 = qc.getQueryData(key_1);
      for (const e of buffered_0) {
        const custom_1 = snapshotSequenceRef.current?.(cached_1, e.subject, e.ev);
        const scoped_0 = custom_1 === undefined || custom_1.kind === 'unhandled' ? scopedSnapshotSequence(cached_1, e.subject, e.ev) : custom_1;
        if (hasSnapshotFence(scoped_0, hashKey(key_1))) {
          applyNow(e.subject, e.ev);
          if (process.env.NODE_ENV !== 'production') _bumpPerf('buffer.flushed');
          continue;
        }
        emitHandoffRecovery(specNameRef.current, scoped_0.kind === 'invalid' ? 'malformed_cursor' : 'missing_cursor');
        throttledRealtimeRefetch(qc, key_1);
        if (process.env.NODE_ENV !== 'production') _bumpPerf('buffer.dropped', buffered_0.length);
        break;
      }
    } else if (process.env.NODE_ENV !== 'production') {
      _bumpPerf('buffer.dropped', buffered_0.length);
    }
  }, [qc, querySettled, query.isSuccess, applyNow, handoffScopeHash]);
  // Attach the live-since-mount fence to the query result via a sidecar
  // WeakMap so `createListHook` can read it without spreading `query` (which
  // would defeat TanStack v5's tracked-properties optimisation for
  // single-row hooks). The viewport-gated path still spreads to add `ref`
  // — that's the pre-existing pattern.
  attachFenceToResult(query, liveSinceMountFenceSeq);
  if (opts.viewportGated) {
    return {
      ...query,
      ref: setVisibilityRef
    };
  }
  return query;
}
/**
 * Split per-entity subjects into the always-on set and the viewport-gated
 * fan-out set. Returns the input array itself when nothing is gated so the
 * handoff scope keeps a stable identity.
 */
function partitionGatedSubjects(subjects, gatedKey, gateIdFor) {
  if (gatedKey === '') return {
    alwaysOn: subjects,
    gated: []
  };
  const gatedSet = new Set(gatedKey.split('|'));
  const alwaysOn = [];
  const gated = [];
  for (const subject of subjects) {
    if (!gatedSet.has(subject)) {
      alwaysOn.push(subject);
      continue;
    }
    const custom = gateIdFor(subject);
    const id = custom === undefined ? defaultSubjectGateId(subject) : custom;
    if (id === null) alwaysOn.push(subject);else gated.push(subject);
  }
  return {
    alwaysOn,
    gated
  };
}